import { Component, HostListener, inject, signal, computed, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { ApiService, showToast } from './api.service';
import { forkJoin } from 'rxjs';
import { jobStatusClass } from './job-status-style';

@Component({
  selector: 'app-admin-job-detail',
  standalone: true,
  imports: [CommonModule, RouterLink, FormsModule],
  template: `
    <div class="min-h-full admin-premium-bg">
      <!-- Header -->
      <div class="shrink-0 px-5 md:px-8 pt-5 pb-1">
        <div class="flex flex-col xl:flex-row xl:items-center justify-between gap-3">
          <div class="min-w-0">
            <a [routerLink]="backRoute()" class="inline-flex items-center gap-1.5 text-slate-500 hover:text-[#1A6FA6] font-semibold text-xs mb-1 transition-colors cursor-pointer w-fit">
              <i class="ti ti-arrow-left"></i> Dispatch Board
            </a>
            <div class="flex flex-wrap items-center gap-2">
              <h1 class="text-xl font-semibold text-slate-950 tracking-tight truncate">{{ job()?.id || 'Loading...' }}</h1>
              <span [ngClass]="jobStatusClass(job()?.status)">
                {{ job()?.status || 'Unknown' }}
              </span>
              @if (isArchived()) {
                <span class="px-2 py-0.5 rounded-md text-[10px] font-semibold uppercase border bg-slate-100 text-slate-600 border-slate-200 shadow-sm flex items-center gap-1">
                  <i class="ti ti-archive"></i> Archived
                </span>
              }
              @if (job()?.type) {
                <span class="px-2 py-0.5 rounded-md text-[10px] font-semibold uppercase border flex items-center gap-1"
                      [ngClass]="{
                        'bg-blue-50 text-blue-700 border-blue-100': job()?.type === 'Inspection',
                        'bg-emerald-50 text-emerald-700 border-emerald-100': job()?.type === 'Service',
                        'bg-red-50 text-red-700 border-red-100': job()?.type === 'Emergency'
                      }">
                  <i class="ti text-[12px]"
                     [class.ti-clipboard-search]="job()?.type === 'Inspection'"
                     [class.ti-tool]="job()?.type === 'Service'"
                     [class.ti-alert-triangle]="job()?.type === 'Emergency'"></i>
                  {{ job()?.type }}
                </span>
              }
            </div>
            <p class="text-xs text-slate-500 max-w-3xl truncate">{{ job()?.address || 'Location Hidden' }}</p>
          </div>
          <div class="flex flex-wrap items-center gap-2">
            @if (nextFieldAction(); as action) {
              <button type="button" (click)="changeJobStatus(action.status)" [disabled]="isSaving()"
                 class="h-9 px-3 rounded-lg text-white text-xs font-semibold shadow-sm transition-colors flex items-center gap-1.5 disabled:opacity-60"
                 [ngClass]="action.className">
                <i [class]="'ti ' + action.icon + ' text-sm'"></i> {{ action.label }}
              </button>
            }
            @if (!isArchived()) {
              <a [routerLink]="quoteRoute()" [queryParams]="quoteQueryParams()"
                 class="h-9 px-3 rounded-lg bg-[#1A6FA6] text-white text-xs font-semibold shadow-sm hover:bg-[#0C3A5A] transition-colors flex items-center gap-1.5 no-underline cursor-pointer">
                <i class="ti ti-file-invoice text-sm"></i> Quote
              </a>
            }
            @if (job() && !isArchived() && canManageArchive()) {
              <button type="button" (click)="openArchiveModal()"
                class="h-9 px-3 rounded-lg bg-white border border-slate-200 text-slate-600 text-xs font-semibold shadow-sm hover:bg-slate-50 transition-colors flex items-center gap-1.5">
                <i class="ti ti-archive text-sm"></i> Archive
              </button>
            }
          </div>
        </div>
      </div>
      <!-- Job Timestamp + Customer Strip -->
      @if (job()) {
        @if (isArchived()) {
          <div class="mx-6 md:mx-8 mt-3 rounded-xl border border-slate-200 bg-slate-100 px-4 py-3 flex items-start gap-3">
            <span class="w-9 h-9 rounded-lg bg-white text-slate-500 flex items-center justify-center shrink-0"><i class="ti ti-archive text-lg"></i></span>
            <div>
              <p class="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Archived Job</p>
              <p class="text-sm font-semibold text-slate-800 mt-0.5">{{ job()?.archive_remarks || 'No archive remarks recorded.' }}</p>
              @if (job()?.archived_at) {
                <p class="text-xs text-slate-500 mt-1">{{ formatDate(job().archived_at) }}</p>
              }
              @if (job()?.archived_by_name) {
                <p class="text-xs text-slate-500 mt-1">Archived by {{ job().archived_by_name }}</p>
              }
            </div>
          </div>
        }
        <div class="mx-6 md:mx-8 mt-3 grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
          <div class="bg-white rounded-xl border border-[#E7EEF5] shadow-sm px-4 py-3 flex items-center gap-3">
            <span class="w-9 h-9 rounded-lg bg-[#EAF5FA] text-[#1A6FA6] flex items-center justify-center"><i class="ti ti-user-circle text-lg"></i></span>
            <div class="min-w-0">
              <p class="text-[10px] font-semibold text-slate-400 uppercase tracking-widest">Customer</p>
              <p class="text-sm font-semibold text-slate-800 truncate">{{ customerContact()?.name || '-' }}</p>
              @if (customerContact()?.phone) {
                <a [href]="'tel:' + customerContact()?.phone" class="text-[10px] font-semibold text-[#2B9AD2]">{{ customerContact()?.phone }}</a>
              }
            </div>
          </div>
          @if (job()?.actual_hours !== null && job()?.actual_hours !== undefined) {
            <div class="bg-white rounded-xl border border-[#E7EEF5] shadow-sm px-4 py-3 flex items-center gap-3">
              <span class="w-9 h-9 rounded-lg bg-indigo-50 text-indigo-600 flex items-center justify-center"><i class="ti ti-clock-hour-4 text-lg"></i></span>
              <div>
                <p class="text-[10px] font-semibold text-slate-400 uppercase tracking-widest">Actual Job Hours</p>
                <p class="text-sm font-semibold text-slate-800">{{ job()?.actual_hours | number:'1.2-2' }} hours</p>
                @if (job()?.pricing_type === 'Fixed') { <p class="text-[10px] text-slate-400">Recorded internally, not billed hourly</p> }
              </div>
            </div>
          }
          <div class="bg-white rounded-xl border border-[#E7EEF5] shadow-sm px-4 py-3 flex items-center gap-3">
            <span class="w-9 h-9 rounded-lg bg-slate-50 text-slate-500 flex items-center justify-center"><i class="ti ti-calendar-check text-lg"></i></span>
            <div class="min-w-0 flex-1">
              <p class="text-[10px] font-semibold text-slate-400 uppercase tracking-widest">Scheduled</p>
              <p class="text-sm font-semibold text-slate-800">{{ job()?.scheduled_date ? formatDate(job().scheduled_date) : '-' }}</p>
            </div>
            @if (canScheduleJob()) {
              <button (click)="openScheduleModal()" class="text-[10px] font-bold text-[#1A6FA6] uppercase tracking-widest">Edit</button>
            }
          </div>
          <div class="bg-white rounded-xl border border-[#E7EEF5] shadow-sm px-4 py-3 flex items-center gap-3">
            <span class="w-9 h-9 rounded-lg bg-violet-50 text-violet-600 flex items-center justify-center"><i class="ti ti-cash text-lg"></i></span>
            <div>
              <p class="text-[10px] font-semibold text-slate-400 uppercase tracking-widest">Labour Pricing</p>
              <p class="text-sm font-semibold text-slate-800">
                {{ job()?.pricing_type === 'Fixed' ? ('Fixed $' + ((job()?.fixed_labour_charge || 0) * (job()?.emergency_double_rate == 1 ? 2 : 1))) : 'Hourly labour' }}
              </p>
              @if (job()?.type === 'Emergency' && job()?.emergency_double_rate == 1) { <p class="text-[10px] font-bold text-rose-600">2× emergency labour rate applied</p> }
            </div>
          </div>
          <div class="bg-white rounded-xl border border-[#E7EEF5] shadow-sm px-4 py-3 flex items-center gap-3">
            <span class="w-9 h-9 rounded-lg bg-emerald-50 text-emerald-600 flex items-center justify-center"><i class="ti ti-player-play text-lg"></i></span>
            <div>
              <p class="text-[10px] font-semibold text-slate-400 uppercase tracking-widest">Started</p>
              <p class="text-sm font-semibold" [class.text-emerald-700]="job()?.started_at" [class.text-slate-500]="!job()?.started_at">
                {{ job()?.started_at ? formatDate(job().started_at) : 'Not Started' }}
              </p>
            </div>
          </div>
          <div class="bg-white rounded-xl border border-[#E7EEF5] shadow-sm px-4 py-3 flex items-center gap-3">
            <span class="w-9 h-9 rounded-lg bg-sky-50 text-sky-600 flex items-center justify-center"><i class="ti ti-flag-check text-lg"></i></span>
            <div>
              <p class="text-[10px] font-semibold text-slate-400 uppercase tracking-widest">Field Finished</p>
              <p class="text-sm font-semibold" [class.text-sky-700]="job()?.completed_at" [class.text-slate-500]="!job()?.completed_at">
                {{ job()?.completed_at ? formatDate(job().completed_at) : '-' }}
              </p>
            </div>
          </div>
          @if (job()?.customer_feedback; as customerFeedback) {
            <div class="rounded-xl border shadow-sm px-4 py-3 flex items-center gap-3"
                 [ngClass]="customerFeedback.rating <= 2 ? 'border-rose-100 bg-rose-50' : customerFeedback.rating === 3 ? 'border-amber-100 bg-amber-50' : 'border-emerald-100 bg-emerald-50'">
              <span class="w-9 h-9 rounded-lg bg-white flex items-center justify-center shrink-0"
                    [ngClass]="feedbackStarColour(customerFeedback.rating)"><i class="ti ti-star-filled text-lg"></i></span>
              <div class="min-w-0">
                <p class="text-[10px] font-semibold text-slate-400 uppercase tracking-widest">Customer Feedback</p>
                <div class="flex items-center gap-2">
                  <div class="flex items-center gap-0.5" [attr.aria-label]="customerFeedback.rating + ' out of 5 stars'">
                    @for (star of feedbackStars; track star) {
                      <i class="ti ti-star-filled text-sm"
                         [ngClass]="star <= customerFeedback.rating ? feedbackStarColour(customerFeedback.rating) : 'text-slate-300'"></i>
                    }
                  </div>
                  <span class="text-sm font-bold text-slate-800">{{ customerFeedback.rating }}/5</span>
                </div>
                <p class="text-[10px] text-slate-500 truncate">{{ customerFeedback.comments || customerFeedback.service_category || 'Rating submitted' }}</p>
              </div>
            </div>
          }
          @if (job()?.status === 'Paused') {
            <div class="bg-orange-50 rounded-xl border border-orange-100 shadow-sm px-4 py-3 flex items-center gap-3 sm:col-span-2">
              <span class="w-9 h-9 rounded-lg bg-white text-orange-600 border border-orange-100 flex items-center justify-center"><i class="ti ti-player-pause text-lg"></i></span>
              <div class="min-w-0">
                <p class="text-[10px] font-semibold text-orange-500 uppercase tracking-widest">{{ job()?.pause_category || 'Paused Since' }}</p>
                <p class="text-sm font-semibold text-orange-800">{{ job()?.pause_started_at || job()?.paused_at ? formatDate(job()?.pause_started_at || job()?.paused_at) : 'Not recorded' }}</p>
                <p class="text-xs text-orange-700 mt-0.5 truncate">{{ job()?.pause_remarks || 'No pause remarks recorded.' }}</p>
              </div>
            </div>
          }
          <div class="bg-white rounded-xl border border-[#E7EEF5] shadow-sm px-4 py-3 flex items-center gap-3">
            <span class="w-9 h-9 rounded-lg flex items-center justify-center"
                  [class.bg-emerald-50]="latestSafetyStatus() === 'Accepted'"
                  [class.text-emerald-600]="latestSafetyStatus() === 'Accepted'"
                  [class.bg-rose-50]="latestSafetyStatus() === 'Declined'"
                  [class.text-rose-600]="latestSafetyStatus() === 'Declined'"
                  [class.bg-amber-50]="!job()?.latest_safety_acknowledgement"
                  [class.text-amber-600]="!job()?.latest_safety_acknowledgement">
              <i [class]="latestSafetyStatus() === 'Declined' ? 'ti ti-shield-x text-lg' : 'ti ti-shield-check text-lg'"></i>
            </span>
            <div class="min-w-0">
              <p class="text-[10px] font-semibold text-slate-400 uppercase tracking-widest">Safety Ack</p>
              @if (job()?.latest_safety_acknowledgement) {
                <p class="text-sm font-semibold truncate" [class.text-emerald-700]="latestSafetyStatus() === 'Accepted'" [class.text-rose-700]="latestSafetyStatus() === 'Declined'">
                  {{ latestSafetyStatus() === 'Declined' ? 'Declined' : 'Accepted' }} by {{ job().latest_safety_acknowledgement.worker_name || 'Worker' }}
                </p>
                <p class="text-[10px] font-semibold text-slate-400">{{ formatDate(job().latest_safety_acknowledgement.declined_at || job().latest_safety_acknowledgement.accepted_at) }}</p>
              } @else {
                <p class="text-sm font-semibold text-amber-600">Pending</p>
              }
            </div>
          </div>
          <button (click)="openManageWorkers()" class="bg-white rounded-xl border border-[#E7EEF5] shadow-sm px-4 py-3 flex items-center gap-3 text-left cursor-pointer hover:border-[#9ED4EF] transition-colors">
            <span class="w-9 h-9 rounded-lg bg-[#EAF5FA] text-[#1A6FA6] flex items-center justify-center"><i class="ti ti-users text-lg"></i></span>
            <div class="min-w-0 flex-1">
              <p class="text-[10px] font-semibold text-slate-400 uppercase tracking-widest">Workers</p>
              <p class="text-sm font-semibold text-slate-800 truncate">{{ workerNames() }}</p>
            </div>
            <span class="text-[10px] font-semibold text-[#2B9AD2] uppercase tracking-widest">Manage</span>
          </button>
        </div>

        @if (job()?.work_sessions?.length) {
          <div class="mx-6 md:mx-8 mt-3 rounded-xl border border-[#E7EEF5] bg-white shadow-sm overflow-hidden">
            <div class="px-4 py-3 border-b border-slate-100 flex items-center justify-between gap-3">
              <div>
                <p class="text-[10px] font-bold uppercase tracking-widest text-slate-400">Individual Worker Timing</p>
                <p class="text-xs text-slate-500 mt-0.5">Each worker controls only their own timer.</p>
              </div>
              <div class="text-right">
                <p class="text-sm font-bold text-emerald-700">{{ job()?.active_worker_count || 0 }} active</p>
                <p class="text-[10px] text-orange-600">{{ job()?.paused_worker_count || 0 }} paused</p>
              </div>
            </div>
            <div class="divide-y divide-slate-100">
              @for (session of job()?.work_sessions; track session.id) {
                <div class="px-4 py-3 flex items-center gap-3">
                  <span class="w-9 h-9 rounded-lg flex items-center justify-center"
                        [ngClass]="session.status === 'active' ? 'bg-emerald-50 text-emerald-600' : session.status === 'paused' ? 'bg-orange-50 text-orange-600' : 'bg-slate-50 text-slate-500'">
                    <i [class]="session.status === 'active' ? 'ti ti-player-play-filled' : session.status === 'paused' ? 'ti ti-player-pause-filled' : 'ti ti-player-stop-filled'"></i>
                  </span>
                  <div class="min-w-0 flex-1">
                    <p class="text-sm font-semibold text-slate-800 truncate">{{ session.worker_name }}</p>
                    <p class="text-[11px] text-slate-500">Started {{ formatDate(session.started_at) }} · {{ (session.total_seconds / 3600) | number:'1.2-2' }} recorded hour(s)</p>
                    @if (session.pause_remarks) { <p class="text-[11px] text-orange-700 truncate mt-0.5">{{ session.pause_category }}: {{ session.pause_remarks }}</p> }
                  </div>
                  <span class="px-2 py-1 rounded-md text-[10px] font-bold uppercase"
                        [ngClass]="session.status === 'active' ? 'bg-emerald-50 text-emerald-700' : session.status === 'paused' ? 'bg-orange-50 text-orange-700' : 'bg-slate-100 text-slate-600'">{{ session.status }}</span>
                </div>
              }
            </div>
          </div>
        }

        <div class="mx-6 md:mx-8 mt-3 rounded-xl border px-4 py-3 flex flex-col lg:flex-row lg:items-center justify-between gap-3" [ngClass]="jobControlActionClass()">
          <div>
            <p class="text-[10px] font-bold uppercase tracking-[0.12em] opacity-70">{{ jobControlStageLabel() }}</p>
            <p class="text-[13px] font-semibold mt-0.5">{{ jobControlActionLabel() }}</p>
          </div>
          <div class="flex flex-wrap gap-2">
            @if (job()?.status === 'Approved') {
              <button type="button" (click)="openManageWorkers()" class="h-8 px-3 rounded-md bg-white border border-white text-slate-700 text-[10px] font-bold">Schedule Approved Work</button>
            }
            @if (job()?.status === 'Ready for Office') {
              <button type="button" (click)="switchTab('office')" class="h-8 px-3 rounded-md bg-white border border-white text-slate-700 text-[10px] font-bold">Review Evidence</button>
            }
            @if (job()?.status === 'Paused') {
              <button type="button" (click)="openPauseResolutionModal()" class="h-8 px-3 rounded-md bg-white border border-white text-slate-700 text-[10px] font-bold">Resume with Note</button>
            }
          </div>
        </div>

        @if (job()?.type === 'Inspection' || job()?.linked_inspection_id) {
          <div class="mx-6 md:mx-8 mt-3 rounded-xl border border-sky-100 bg-sky-50 px-4 py-3">
            <div class="flex flex-col lg:flex-row lg:items-center justify-between gap-3">
              <div>
                <p class="text-[10px] font-bold uppercase tracking-[0.12em] text-[#1A6FA6]">Inspection to Service Flow</p>
                <p class="text-[13px] font-semibold text-slate-800 mt-0.5">{{ inspectionFlowStatusLabel() }}</p>
              </div>
              <div class="flex flex-wrap gap-1.5">
                @for (step of inspectionFlowSteps(); track step.label) {
                  <span class="px-2 py-1 rounded-md border text-[10px] font-bold" [ngClass]="step.done ? 'bg-emerald-50 text-emerald-700 border-emerald-100' : 'bg-white text-slate-500 border-slate-200'">{{ step.label }}</span>
                }
              </div>
            </div>
          </div>
        }
        @if (showArchiveModal()) {
          <div class="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <div class="bg-white rounded-3xl shadow-xl w-full max-w-lg border border-slate-100 overflow-hidden">
              <div class="p-5 border-b border-slate-100 flex items-center justify-between bg-slate-50/50">
                <div>
                  <h2 class="text-lg font-bold text-slate-900">Archive Job</h2>
                  <p class="text-xs text-slate-500 font-medium">This removes the job from active operational lists.</p>
                </div>
                <button (click)="closeArchiveModal()" class="w-9 h-9 rounded-full hover:bg-slate-200 flex items-center justify-center text-slate-400">
                  <i class="ti ti-x"></i>
                </button>
              </div>
              <div class="p-5">
                <label class="block">
                  <span class="text-sm font-semibold text-slate-700 mb-1 block">Archive remarks *</span>
                  <textarea [(ngModel)]="archiveRemarks" rows="4" maxlength="2000" class="w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm outline-none focus:bg-white focus:border-[#1A6FA6] resize-none" placeholder="Example: duplicate job, cancelled by customer, moved to project..."></textarea>
                </label>
              </div>
              <div class="p-5 border-t border-slate-100 bg-slate-50/50 flex justify-end gap-3">
                <button (click)="closeArchiveModal()" class="h-10 px-4 rounded-xl text-slate-600 text-sm font-bold">Cancel</button>
                <button (click)="archiveJob()" [disabled]="isSaving() || archiveRemarks.trim().length < 5" class="h-10 px-5 rounded-xl bg-slate-800 text-white text-sm font-bold disabled:opacity-50">
                  @if (isSaving()) { Archiving... } @else { Archive Job }
                </button>
              </div>
            </div>
          </div>
        }
        @if (showPauseResolutionModal()) {
          <div class="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <div class="bg-white rounded-3xl shadow-xl w-full max-w-lg border border-slate-100 overflow-hidden">
              <div class="p-5 border-b border-slate-100 flex items-center justify-between bg-slate-50/50">
                <div>
                  <h2 class="text-lg font-bold text-slate-900">Resume Paused Job</h2>
                  <p class="text-xs text-slate-500 font-medium">Record what cleared the blocked work before sending it back to field work.</p>
                </div>
                <button (click)="closePauseResolutionModal()" class="w-9 h-9 rounded-full hover:bg-slate-200 flex items-center justify-center text-slate-400">
                  <i class="ti ti-x"></i>
                </button>
              </div>
              <div class="p-5 space-y-4">
                <div class="rounded-xl bg-orange-50 border border-orange-100 px-4 py-3">
                  <p class="text-[10px] font-bold uppercase tracking-widest text-orange-600">{{ job()?.pause_category || 'Pause Reason' }}</p>
                  <p class="text-sm font-semibold text-orange-900 mt-1">{{ job()?.pause_remarks || 'No pause remarks recorded.' }}</p>
                </div>
                <label class="block">
                  <span class="text-sm font-semibold text-slate-700 mb-1 block">Resolution note *</span>
                  <textarea [(ngModel)]="pauseResolutionNotes" rows="4" class="w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm outline-none focus:bg-white focus:border-[#1A6FA6] resize-none" placeholder="Example: parts arrived, customer onsite, safety concern cleared..."></textarea>
                </label>
              </div>
              <div class="p-5 border-t border-slate-100 bg-slate-50/50 flex justify-end gap-3">
                <button (click)="closePauseResolutionModal()" class="h-10 px-4 rounded-xl text-slate-600 text-sm font-bold">Cancel</button>
                <button (click)="resumePausedJob()" [disabled]="isSaving() || pauseResolutionNotes.trim().length < 5" class="h-10 px-5 rounded-xl bg-emerald-600 text-white text-sm font-bold disabled:opacity-50">
                  @if (isSaving()) { Resuming... } @else { Resume Job }
                </button>
              </div>
            </div>
          </div>
        }
        @if (showCloseoutModal()) {
          <div class="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <div class="bg-white rounded-3xl shadow-xl w-full max-w-xl border border-slate-100 overflow-hidden">
              <div class="p-5 border-b border-slate-100 flex items-center justify-between bg-slate-50/50">
                <div>
                  <h2 class="text-lg font-bold text-slate-900">Complete Job</h2>
                  <p class="text-xs text-slate-500 font-medium">Record the final office close-out note before closing this job.</p>
                </div>
                <button (click)="closeCloseoutModal()" class="w-9 h-9 rounded-full hover:bg-slate-200 flex items-center justify-center text-slate-400">
                  <i class="ti ti-x"></i>
                </button>
              </div>
              <div class="p-5 space-y-4">
                @if (officeBlockers().length > 0) {
                  <div class="rounded-xl border border-amber-100 bg-amber-50 px-4 py-3">
                    <p class="text-xs font-bold text-amber-900">Completion exceptions required</p>
                    <div class="mt-2 space-y-1">
                      @for (blocker of officeBlockers(); track blocker.key) {
                        <p class="text-xs text-amber-800">- {{ blocker.detail }}</p>
                      }
                    </div>
                  </div>
                }
                <label class="block">
                  <span class="text-sm font-semibold text-slate-700 mb-1 block">Final close-out note *</span>
                  <textarea [(ngModel)]="officeCloseoutNote" rows="4" class="w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm outline-none focus:bg-white focus:border-[#1A6FA6] resize-none" placeholder="Example: evidence reviewed, materials checked, job ready to close..."></textarea>
                </label>
                @if (officeBlockers().length > 0) {
                  <label class="block">
                    <span class="text-sm font-semibold text-slate-700 mb-1 block">Exception remarks *</span>
                    <textarea [(ngModel)]="officeExceptionRemarks" rows="4" class="w-full rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-sm outline-none focus:bg-white focus:border-amber-400 resize-none" placeholder="Example: no photos required because this was phone-confirmed labour-only work..."></textarea>
                  </label>
                }
              </div>
              <div class="p-5 border-t border-slate-100 bg-slate-50/50 flex justify-end gap-3">
                <button (click)="closeCloseoutModal()" class="h-10 px-4 rounded-xl text-slate-600 text-sm font-bold">Cancel</button>
                <button (click)="submitCloseout()" [disabled]="isSaving() || closeoutInvalid()" class="h-10 px-5 rounded-xl bg-emerald-600 text-white text-sm font-bold disabled:opacity-50">
                  @if (isSaving()) { Completing... } @else { Complete Job }
                </button>
              </div>
            </div>
          </div>
        }
        <!-- Manage Workers Modal -->
        @if (showScheduleModal()) {
          <div class="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <div class="bg-white rounded-3xl shadow-xl w-full max-w-md border border-slate-100 overflow-hidden">
              <div class="p-5 border-b border-slate-100 flex items-center justify-between bg-slate-50/50">
                <div>
                  <h2 class="text-lg font-bold text-slate-900">Schedule Job</h2>
                  <p class="text-xs text-slate-500 font-medium">Set the planned field attendance time.</p>
                </div>
                <button (click)="closeScheduleModal()" class="w-9 h-9 rounded-full hover:bg-slate-200 flex items-center justify-center text-slate-400">
                  <i class="ti ti-x"></i>
                </button>
              </div>
              <div class="p-5 space-y-4">
                <label class="block">
                  <span class="text-sm font-semibold text-slate-700 mb-1 block">Date</span>
                  <input type="date" [(ngModel)]="scheduleDate" class="w-full h-11 px-3 rounded-xl border border-slate-200 bg-slate-50 outline-none focus:bg-white focus:border-[#1A6FA6]">
                </label>
                <label class="block">
                  <span class="text-sm font-semibold text-slate-700 mb-1 block">Time</span>
                  <input type="time" [(ngModel)]="scheduleTime" class="w-full h-11 px-3 rounded-xl border border-slate-200 bg-slate-50 outline-none focus:bg-white focus:border-[#1A6FA6]">
                </label>
                @if (scheduleChangeNeedsReason(scheduleDate && scheduleTime ? scheduleDate + ' ' + scheduleTime + ':00' : null)) {
                  <div class="rounded-xl border border-amber-100 bg-amber-50 p-3 space-y-3">
                    <p class="text-xs font-bold text-amber-800">Schedule change reason required</p>
                    <select [(ngModel)]="scheduleChangeCategory" class="w-full h-10 rounded-lg border border-amber-100 bg-white px-3 text-sm font-semibold text-slate-700 outline-none">
                      @for (category of scheduleChangeCategories; track category) {
                        <option [value]="category">{{ category }}</option>
                      }
                    </select>
                    <textarea [(ngModel)]="scheduleChangeRemarks" rows="3" placeholder="Why is this schedule changing?"
                              class="w-full rounded-lg border border-amber-100 bg-white px-3 py-2 text-sm outline-none resize-none"></textarea>
                  </div>
                }
              </div>
              <div class="p-5 border-t border-slate-100 bg-slate-50/50 flex justify-between gap-3">
                <button (click)="clearSchedule()" [disabled]="isSaving() || scheduleReasonInvalid(null)" class="h-10 px-4 rounded-xl bg-white border border-slate-200 text-slate-600 text-sm font-bold disabled:opacity-50">Clear</button>
                <div class="flex gap-3">
                  <button (click)="closeScheduleModal()" class="h-10 px-4 rounded-xl text-slate-600 text-sm font-bold">Cancel</button>
                  <button (click)="saveSchedule()" [disabled]="isSaving() || !scheduleDate || !scheduleTime || scheduleReasonInvalid(scheduleDate + ' ' + scheduleTime + ':00')" class="h-10 px-5 rounded-xl bg-[#1A6FA6] text-white text-sm font-bold disabled:opacity-50">
                    @if (isSaving()) { Saving... } @else { Save }
                  </button>
                </div>
              </div>
            </div>
          </div>
        }

        <!-- Manage Workers Modal -->
        @if (showManageWorkers()) {
          <div class="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <div class="bg-white rounded-3xl shadow-xl w-full max-w-2xl border border-slate-100 overflow-hidden flex flex-col max-h-[90vh]">
              <div class="p-6 border-b border-slate-100 flex items-center justify-between bg-slate-50/50">
                <div>
                  <h2 class="text-xl font-bold text-slate-900">Manage Team Assignment</h2>
                  <p class="text-xs text-slate-500 font-medium">Select one or more plumbers for this job.</p>
                </div>
                <button (click)="closeManageWorkers()" class="w-10 h-10 rounded-full hover:bg-slate-200 flex items-center justify-center text-slate-400 transition-colors">
                  <i class="ti ti-x text-xl"></i>
                </button>
              </div>
              
              <div class="p-6 overflow-y-auto flex-1">
                <div class="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4 rounded-2xl border border-slate-100 bg-slate-50/70 p-4">
                  <label class="block">
                    <span class="text-[10px] font-bold text-slate-500 uppercase tracking-widest block mb-1">Scheduled date</span>
                    <input type="date" [(ngModel)]="scheduleDate" (ngModelChange)="loadDispatchCalendar()" class="w-full h-10 rounded-xl border border-slate-200 bg-white px-3 text-sm outline-none focus:border-[#2B9AD2]">
                  </label>
                  <label class="block">
                    <span class="text-[10px] font-bold text-slate-500 uppercase tracking-widest block mb-1">Time</span>
                    <input type="time" [(ngModel)]="scheduleTime" class="w-full h-10 rounded-xl border border-slate-200 bg-white px-3 text-sm outline-none focus:border-[#2B9AD2]">
                  </label>
                  <p class="md:col-span-2 text-xs font-semibold text-slate-400">Team updates for unstarted jobs must include a scheduled attendance time.</p>
                  @if (scheduleChangeNeedsReason(scheduleDate && scheduleTime ? scheduleDate + ' ' + scheduleTime + ':00' : null)) {
                    <div class="md:col-span-2 rounded-xl border border-amber-100 bg-amber-50 p-3 space-y-2">
                      <p class="text-xs font-bold text-amber-800">Reschedule reason</p>
                      <select [(ngModel)]="scheduleChangeCategory" class="w-full h-10 rounded-lg border border-amber-100 bg-white px-3 text-sm font-semibold text-slate-700 outline-none">
                        @for (category of scheduleChangeCategories; track category) {
                          <option [value]="category">{{ category }}</option>
                        }
                      </select>
                      <textarea [(ngModel)]="scheduleChangeRemarks" rows="3" placeholder="Why is the booking changing?"
                                class="w-full rounded-lg border border-amber-100 bg-white px-3 py-2 text-sm outline-none resize-none"></textarea>
                    </div>
                  }
                </div>
                <div class="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
                  @for (stat of dispatchStats(); track stat.label) {
                    <div class="rounded-2xl border border-slate-100 bg-white px-4 py-3 shadow-sm">
                      <p class="text-[10px] font-bold text-slate-400 uppercase tracking-widest">{{ stat.label }}</p>
                      <p class="text-lg font-black mt-0.5" [ngClass]="stat.className">{{ stat.value }}</p>
                      <p class="text-[11px] text-slate-500 mt-0.5">{{ stat.help }}</p>
                    </div>
                  }
                </div>
                <div class="grid grid-cols-2 sm:grid-cols-3 gap-4">
                  @for (worker of sortedDispatchWorkers(); track worker.id) {
                    <button (click)="toggleWorkerSelection(worker.id)" 
                      class="p-4 rounded-2xl border-2 transition-all flex flex-col items-center text-center gap-3 relative"
                      [class]="workerCardClass(worker)">
                      
                      @if (isWorkerSelected(worker.id)) {
                        <div class="absolute top-3 right-3 w-5 h-5 rounded-full bg-[#2B9AD2] text-white flex items-center justify-center shadow-sm">
                          <i class="ti ti-check text-[12px]"></i>
                        </div>
                      }

                      <div class="w-12 h-12 rounded-2xl flex items-center justify-center font-bold text-lg shadow-sm"
                        [class]="isWorkerSelected(worker.id) ? 'bg-[#2B9AD2] text-white' : 'bg-white text-slate-300 border border-slate-100'">
                        {{ worker.name.charAt(0) }}
                      </div>
                      
                      <div>
                        <p class="text-sm font-bold leading-tight" [class.text-[#2B9AD2]]="isWorkerSelected(worker.id)">{{ worker.name }}</p>
                        <p class="text-[10px] font-bold text-slate-400 mt-1 uppercase tracking-widest">{{ worker.role === 'Worker' ? 'Plumber' : worker.role }}</p>
                      </div>
                      <div class="w-full space-y-1 text-left">
                        @for (hint of dispatchWorkerHints(worker); track hint.label) {
                          <div class="flex items-center justify-between gap-2 rounded-lg px-2 py-1" [ngClass]="hint.className">
                            <span class="text-[10px] font-bold truncate">{{ hint.label }}</span>
                            <i class="ti text-[12px]" [class]="hint.icon"></i>
                          </div>
                        }
                      </div>
                    </button>
                  }
                </div>
                @if (selectedWorkerIds().length) {
                  <div class="mt-4 rounded-2xl border border-slate-100 bg-slate-50/70 p-4">
                    <h4 class="text-xs font-bold uppercase tracking-widest text-slate-500 mb-3">Optional Fleet</h4>
                    <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
                      @for (id of selectedWorkerIds(); track id) {
                        <label class="block">
                          <span class="text-[10px] font-bold text-slate-500 block mb-1">{{ workerName(id) }}</span>
                          <select [(ngModel)]="selectedFleetVehicleIds[id]" class="w-full h-10 rounded-xl border border-slate-200 bg-white px-3 text-sm outline-none focus:border-[#2B9AD2]">
                            <option value="">No vehicle</option>
                            @for (vehicle of fleetVehicles(); track vehicle.id) {
                              <option [value]="vehicle.id">{{ fleetVehicleLabel(vehicle) }}</option>
                            }
                          </select>
                        </label>
                      }
                    </div>
                    @if (fleetVehicles().length === 0) {
                      <p class="text-xs font-semibold text-slate-400 mt-2">No active fleet vehicles available.</p>
                    }
                  </div>
                }
              </div>

              <div class="p-6 border-t border-slate-100 bg-slate-50/50 flex justify-end gap-3">
                <button (click)="closeManageWorkers()" class="px-6 py-2.5 rounded-xl font-bold text-slate-600 hover:bg-slate-200 transition-all">Cancel</button>
                <button (click)="saveAssignments()" [disabled]="isSaving() || (selectedWorkerIds().length > 0 && (!scheduleDate || !scheduleTime))" class="px-8 py-2.5 rounded-xl font-bold text-white shadow-sm hover:opacity-90 transition-all disabled:opacity-50 flex items-center gap-2" style="background: linear-gradient(135deg, #2B9AD2, #1A6FA6);">
                  @if (isSaving()) { <i class="ti ti-loader animate-spin text-lg"></i> Saving... }
                  @else { <i class="ti ti-device-floppy text-lg"></i> Update Team }
                </button>
              </div>
            </div>
          </div>
        }

        <!-- Description -->
        @if (job()?.desc || job()?.description) {
          <div class="mx-6 md:mx-8 mt-3 bg-white rounded-2xl border border-slate-200 px-5 py-4">
            <p class="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1">Job Description</p>
            <p class="text-sm text-slate-700 leading-relaxed">{{ job()?.desc || job()?.description }}</p>
          </div>
        }

        @if (job()?.latest_safety_acknowledgement) {
          <div class="mx-6 md:mx-8 mt-3 rounded-2xl border px-5 py-4"
               [class.bg-emerald-50]="latestSafetyStatus() === 'Accepted'"
               [class.border-emerald-100]="latestSafetyStatus() === 'Accepted'"
               [class.bg-rose-50]="latestSafetyStatus() === 'Declined'"
               [class.border-rose-100]="latestSafetyStatus() === 'Declined'">
            <div class="flex items-start gap-3">
              <i [class]="latestSafetyStatus() === 'Declined' ? 'ti ti-shield-x text-rose-600 text-xl mt-0.5' : 'ti ti-shield-check text-emerald-600 text-xl mt-0.5'"></i>
              <div class="flex-1">
                <div class="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <p class="text-[10px] font-bold uppercase tracking-widest mb-1" [class.text-emerald-700]="latestSafetyStatus() === 'Accepted'" [class.text-rose-700]="latestSafetyStatus() === 'Declined'">Pre-start Safety Acknowledgement</p>
                    <p class="text-sm leading-relaxed" [class.text-emerald-900]="latestSafetyStatus() === 'Accepted'" [class.text-rose-900]="latestSafetyStatus() === 'Declined'">{{ job().latest_safety_acknowledgement.statement }}</p>
                  </div>
                  @if (latestSafetyStatus() === 'Declined' && !isArchived()) {
                    <div class="flex flex-wrap gap-2">
                      @if (!job().latest_safety_acknowledgement.resolved_at) {
                        <button (click)="sendSafetyDeclineEmail()" [disabled]="isSendingSafetyEmail() || isResolvingSafety()"
                                class="h-9 px-3 rounded-lg bg-rose-600 text-white text-xs font-bold disabled:opacity-60 flex items-center gap-2">
                          @if (isSendingSafetyEmail()) { <i class="ti ti-loader animate-spin"></i> } @else { <i class="ti ti-mail"></i> }
                          Email Customer
                        </button>
                        <button (click)="resolveSafetyConcern()" [disabled]="isSendingSafetyEmail() || isResolvingSafety()"
                                class="h-9 px-3 rounded-lg bg-emerald-600 text-white text-xs font-bold disabled:opacity-60 flex items-center gap-2">
                          @if (isResolvingSafety()) { <i class="ti ti-loader animate-spin"></i> } @else { <i class="ti ti-check"></i> }
                          Mark Resolved
                        </button>
                      }
                    </div>
                  }
                </div>
                @if (latestSafetyStatus() === 'Declined') {
                  <div class="mt-3 rounded-xl bg-white/70 border border-rose-100 px-4 py-3">
                    <p class="text-[10px] font-bold text-rose-700 uppercase tracking-widest mb-1">Decline Reason</p>
                    <p class="text-sm text-rose-900 leading-relaxed">{{ job().latest_safety_acknowledgement.decline_reason || '-' }}</p>
                    @if (job().latest_safety_acknowledgement.decline_photo_data) {
                      <img [src]="job().latest_safety_acknowledgement.decline_photo_data" alt="Safety concern photo" class="mt-3 max-h-72 rounded-xl border border-rose-100 object-contain bg-white">
                    }
                    @if (job().latest_safety_acknowledgement.customer_email_sent_at) {
                      <p class="text-xs text-rose-700 font-bold mt-3">Customer emailed {{ formatDate(job().latest_safety_acknowledgement.customer_email_sent_at) }}</p>
                    }
                    @if (job().latest_safety_acknowledgement.resolved_at) {
                      <div class="mt-3 rounded-lg bg-emerald-50 border border-emerald-100 px-3 py-2 text-emerald-800">
                        <p class="text-xs font-bold">Resolved {{ formatDate(job().latest_safety_acknowledgement.resolved_at) }} by {{ job().latest_safety_acknowledgement.resolved_by_name || 'Office' }}</p>
                        @if (job().latest_safety_acknowledgement.resolution_notes) {
                          <p class="text-xs mt-1">{{ job().latest_safety_acknowledgement.resolution_notes }}</p>
                        }
                      </div>
                    }
                  </div>
                }
                <p class="text-xs font-bold mt-2" [class.text-emerald-700]="latestSafetyStatus() === 'Accepted'" [class.text-rose-700]="latestSafetyStatus() === 'Declined'">
                  {{ latestSafetyStatus() === 'Declined' ? 'Declined' : 'Accepted' }} by {{ job().latest_safety_acknowledgement.worker_name || 'Worker' }}
                  on {{ formatDate(job().latest_safety_acknowledgement.declined_at || job().latest_safety_acknowledgement.accepted_at) }}
                </p>
              </div>
            </div>
          </div>
        }
      }

      <!-- Nav Tabs -->
      <div class="px-6 md:px-8 mt-5">
        <div class="flex gap-1 bg-white border border-[#E7EEF5] rounded-xl p-1 overflow-x-auto w-fit max-w-full shadow-sm">
          <button (click)="switchTab('photos')"
            class="px-3 py-2 rounded-lg text-xs font-semibold transition-colors whitespace-nowrap shrink-0 cursor-pointer"
            [class.bg-[#EAF5FA]]="activeTab() === 'photos'" [class.text-[#1A6FA6]]="activeTab() === 'photos'"
            [class.text-slate-500]="activeTab() !== 'photos'">
            <i class="ti ti-camera mr-1"></i> Photos
          </button>
          <button (click)="switchTab('notes')"
            class="px-3 py-2 rounded-lg text-xs font-semibold transition-colors whitespace-nowrap shrink-0 cursor-pointer"
            [class.bg-[#EAF5FA]]="activeTab() === 'notes'" [class.text-[#1A6FA6]]="activeTab() === 'notes'"
            [class.text-slate-500]="activeTab() !== 'notes'">
            <i class="ti ti-notes mr-1"></i> Notes
          </button>
          <button (click)="switchTab('materials')"
            class="px-3 py-2 rounded-lg text-xs font-semibold transition-colors whitespace-nowrap shrink-0 cursor-pointer"
            [class.bg-[#EAF5FA]]="activeTab() === 'materials'" [class.text-[#1A6FA6]]="activeTab() === 'materials'"
            [class.text-slate-500]="activeTab() !== 'materials'">
            <i class="ti ti-box mr-1"></i> Materials
          </button>
          <button (click)="switchTab('quote')"
            class="px-3 py-2 rounded-lg text-xs font-semibold transition-colors whitespace-nowrap shrink-0 cursor-pointer"
            [class.bg-[#EAF5FA]]="activeTab() === 'quote'" [class.text-[#1A6FA6]]="activeTab() === 'quote'"
            [class.text-slate-500]="activeTab() !== 'quote'">
            <i class="ti ti-file-invoice mr-1"></i> Quote
          </button>
          <button (click)="switchTab('office')"
            class="px-3 py-2 rounded-lg text-xs font-semibold transition-colors whitespace-nowrap shrink-0 cursor-pointer"
            [class.bg-[#EAF5FA]]="activeTab() === 'office'" [class.text-[#1A6FA6]]="activeTab() === 'office'"
            [class.text-slate-500]="activeTab() !== 'office'">
            <i class="ti ti-clipboard-data mr-1"></i> Office Review
          </button>
          <button (click)="switchTab('timeline')"
            class="px-3 py-2 rounded-lg text-xs font-semibold transition-colors whitespace-nowrap shrink-0 cursor-pointer"
            [class.bg-[#EAF5FA]]="activeTab() === 'timeline'" [class.text-[#1A6FA6]]="activeTab() === 'timeline'"
            [class.text-slate-500]="activeTab() !== 'timeline'">
            <i class="ti ti-history mr-1"></i> Timeline
          </button>
        </div>
      </div>

      <!-- Content Area -->
      <div class="p-6 md:p-8 pb-10">
        
        @if (isLoading()) {
          <div class="h-64 flex flex-col items-center justify-center">
             <i class="ti ti-loader animate-spin text-[32px] text-[#2B9AD2] mb-4"></i>
             <p class="text-slate-500 font-bold">Retrieving job records...</p>
          </div>
        } @else {

          <!-- PHOTOS TAB -->
          @if (activeTab() === 'photos') {
            <div class="mb-6 flex justify-between items-center">
              <h2 class="text-lg font-bold text-slate-900">Site Photos</h2>
              <div class="text-sm text-slate-500 font-bold">{{ photos().length }} Photos</div>
            </div>
            @if (photos().length === 0) {
              <div class="bg-white rounded-3xl p-12 text-center border border-dashed border-slate-300 shadow-sm">
                <i class="ti ti-camera text-[48px] text-slate-300 mb-4 block"></i>
                <h3 class="text-lg font-bold text-slate-900 mb-2">No Photos Uploaded</h3>
                <p class="text-slate-500">Workers haven't uploaded any site photos yet.</p>
              </div>
            } @else {
              <div class="grid grid-cols-2 md:grid-cols-3 gap-5">
                @for (photo of photos(); track photo.id; let i = $index) {
                  <button type="button" (click)="openPhotoViewer(i)" class="group bg-white rounded-2xl overflow-hidden shadow-sm border border-slate-100 text-left hover:shadow-md hover:border-[#BEE7F9] transition-all focus:outline-none focus:ring-2 focus:ring-[#2B9AD2]/40">
                    <div class="relative">
                      <img [src]="photo.photo_data" [alt]="photo.caption || 'Site photo'" class="w-full h-48 object-cover">
                      <div class="absolute inset-0 bg-slate-950/0 group-hover:bg-slate-950/30 transition-colors flex items-center justify-center">
                        <span class="opacity-0 group-hover:opacity-100 transition-opacity h-10 px-3 rounded-xl bg-white/95 text-slate-900 text-xs font-bold flex items-center gap-2 shadow-sm">
                          <i class="ti ti-arrows-maximize"></i> View
                        </span>
                      </div>
                    </div>
                    <div class="p-4">
                      @if (photo.caption) { <p class="text-sm font-bold text-slate-800 truncate mb-1">{{ photo.caption }}</p> }
                      <p class="text-xs text-slate-400 font-medium">{{ photo.uploader_name }} - {{ formatDate(photo.created_at) }}</p>
                    </div>
                  </button>
                }
              </div>
            }
          }

          <!-- NOTES TAB -->
          @if (activeTab() === 'notes') {
            <div class="mb-6 flex justify-between items-center">
              <h2 class="text-lg font-bold text-slate-900">Job Notes</h2>
              <div class="text-sm text-slate-500 font-bold">{{ notes().length }} Notes</div>
            </div>
            @if (notes().length === 0) {
              <div class="bg-white rounded-3xl p-12 text-center border border-dashed border-slate-300 shadow-sm">
                <i class="ti ti-notes text-[48px] text-slate-300 mb-4 block"></i>
                <h3 class="text-lg font-bold text-slate-900 mb-2">No Notes Yet</h3>
                <p class="text-slate-500">Workers haven't added any notes for this job.</p>
              </div>
            } @else {
              <div class="space-y-4">
                @for (note of notes(); track note.id) {
                  <div class="bg-white rounded-2xl p-5 shadow-sm border border-slate-100">
                    <div class="flex items-start justify-between mb-3">
                      <div class="flex items-center gap-3">
                        <div class="w-10 h-10 rounded-full bg-orange-100 text-orange-600 flex items-center justify-center font-bold text-sm shrink-0">
                          {{ note.author_name?.charAt(0) || '?' }}
                        </div>
                        <div>
                          <p class="font-bold text-slate-900">{{ note.author_name }}</p>
                          <p class="text-xs text-slate-400">{{ note.author_role }}</p>
                        </div>
                      </div>
                      <p class="text-xs text-slate-400 font-medium shrink-0">{{ formatDate(note.created_at) }}</p>
                    </div>
                    <p class="text-sm text-slate-700 leading-relaxed bg-slate-50 rounded-xl p-4">{{ note.content }}</p>
                  </div>
                }
              </div>
            }
          }

          <!-- MATERIALS TAB -->
          @if (activeTab() === 'materials') {
            <div class="mb-6 flex justify-between items-center">
              <h2 class="text-lg font-bold text-slate-900">Field Materials</h2>
              <div class="text-sm text-slate-500 font-bold">{{ job()?.materials?.length || 0 }} Items</div>
            </div>
            @if (!job()?.materials || job()?.materials?.length === 0) {
              <div class="bg-white rounded-3xl p-12 text-center border border-dashed border-slate-300 shadow-sm">
                <i class="ti ti-box-off text-[48px] text-slate-300 mb-4 block"></i>
                <h3 class="text-lg font-bold text-slate-900 mb-2">No Materials Logged</h3>
                <p class="text-slate-500">Materials added from the mobile job screen will appear here for office review.</p>
              </div>
            } @else {
              <div class="bg-white rounded-3xl border border-slate-200 shadow-sm overflow-hidden">
                <table class="w-full text-sm">
                  <thead class="bg-slate-50 text-[10px] uppercase tracking-widest text-slate-400 font-bold">
                    <tr>
                      <th class="text-left px-5 py-3">Item</th>
                      <th class="text-right px-5 py-3">Qty</th>
                      <th class="text-left px-5 py-3">Unit</th>
                      <th class="text-left px-5 py-3">Readiness</th>
                      <th class="text-right px-5 py-3">Sell Price</th>
                      <th class="text-right px-5 py-3">Line Total</th>
                    </tr>
                  </thead>
                  <tbody class="divide-y divide-slate-100">
                    @for (mat of job()?.materials || []; track mat.id) {
                      <tr>
                        <td class="px-5 py-4 font-bold text-slate-800">{{ mat.item_name }}</td>
                        <td class="px-5 py-4 text-right text-slate-600">{{ mat.quantity || 0 }}</td>
                        <td class="px-5 py-4 text-slate-500">{{ mat.unit || 'EA' }}</td>
                        <td class="px-5 py-4 min-w-[220px]">
                          <div class="flex flex-col gap-2">
                            <select [ngModel]="mat.readiness_status || 'Used'" (ngModelChange)="updateMaterialReadiness(mat, $event)" [disabled]="isArchived()"
                                    class="h-9 rounded-lg border border-slate-200 bg-white px-2 text-xs font-bold outline-none focus:border-[#1A6FA6]">
                              @for (status of materialReadinessStatuses; track status) {
                                <option [value]="status">{{ status }}</option>
                              }
                            </select>
                            @if (mat.readiness_note) {
                              <p class="text-[11px] text-slate-500 leading-snug">{{ mat.readiness_note }}</p>
                            }
                          </div>
                        </td>
                        <td class="px-5 py-4 text-right text-slate-600">{{ materialPrice(mat) | currency:'NZD' }}</td>
                        <td class="px-5 py-4 text-right font-black text-slate-900">{{ materialLineTotal(mat) | currency:'NZD' }}</td>
                      </tr>
                    }
                  </tbody>
                  <tfoot class="bg-slate-50">
                    <tr>
                      <td colspan="5" class="px-5 py-4 text-right font-bold text-slate-500">Materials Total</td>
                      <td class="px-5 py-4 text-right font-black text-[#1A6FA6]">{{ materialsTotal() | currency:'NZD' }}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            }
          }

          <!-- QUOTE TAB -->
          @if (activeTab() === 'quote') {
            <div class="bg-white rounded-3xl border border-slate-200 shadow-sm p-6">
              <div class="flex flex-wrap items-start justify-between gap-4">
                <div>
                  <h2 class="text-lg font-bold text-slate-900">Quote & Customer Approval</h2>
                  <p class="text-sm text-slate-500 mt-1">Create or review the quote linked to this job.</p>
                </div>
                @if (!isArchived()) {
                <a [routerLink]="quoteRoute()" [queryParams]="quoteQueryParams()"
                   class="px-5 py-2.5 bg-[#2B9AD2] text-white rounded-xl font-bold shadow-sm hover:bg-[#1B78AE] transition-all flex items-center gap-2 no-underline">
                  <i class="ti ti-file-invoice text-lg"></i> {{ job()?.quote?.id ? 'Open Quote' : 'Build Quote' }}
                </a>
                }
                @if (job()?.type === 'Inspection' && !isArchived()) {
                  <button (click)="createServiceFromInspection()" [disabled]="isSaving() || inspectionNoServiceRequired() || linkedServiceJobs().length > 0"
                     class="px-5 py-2.5 rounded-xl font-bold shadow-sm transition-all flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                     [ngClass]="inspectionNeedsServiceDecision() ? 'bg-emerald-600 text-white hover:bg-emerald-700' : 'bg-white border border-slate-200 text-slate-700 hover:bg-slate-50'">
                    <i class="ti ti-tool text-lg"></i> Create Linked Service Job
                  </button>
                  @if (inspectionNeedsServiceDecision()) {
                    <button (click)="openNoServiceModal()" [disabled]="isSaving()"
                       class="px-5 py-2.5 bg-white border border-slate-200 text-slate-700 rounded-xl font-bold shadow-sm hover:bg-slate-50 transition-all flex items-center gap-2">
                      <i class="ti ti-circle-off text-lg"></i> No Service Required
                    </button>
                  }
                }
                @if (job()?.type === 'Service' && job()?.linked_inspection_id) {
                  <a [routerLink]="['/admin/dispatch/job', job().linked_inspection_id]"
                     class="px-5 py-2.5 bg-white border border-sky-100 text-[#1A6FA6] rounded-xl font-bold shadow-sm hover:bg-sky-50 transition-all flex items-center gap-2 no-underline">
                    <i class="ti ti-clipboard-search text-lg"></i> Open Inspection Job
                  </a>
                }
              </div>

              @if (job()?.quote) {
                <div class="grid grid-cols-1 md:grid-cols-4 gap-4 mt-6">
                  <div class="bg-slate-50 rounded-2xl p-4 border border-slate-100">
                    <p class="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Quote No</p>
                    <p class="text-lg font-black text-slate-900 mt-1">{{ job().quote.quote_no || 'Draft' }}</p>
                  </div>
                  <div class="bg-slate-50 rounded-2xl p-4 border border-slate-100">
                    <p class="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Status</p>
                    <p class="text-lg font-black text-slate-900 mt-1">{{ job().quote.status || 'Draft' }}</p>
                  </div>
                  <div class="bg-slate-50 rounded-2xl p-4 border border-slate-100">
                    <p class="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Signature</p>
                    <p class="text-lg font-black mt-1" [class.text-emerald-600]="job().quote.signature_data" [class.text-amber-600]="!job().quote.signature_data">{{ job().quote.signature_data ? 'Signed' : 'Pending' }}</p>
                  </div>
                  <div class="bg-slate-50 rounded-2xl p-4 border border-slate-100">
                    <p class="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Job State</p>
                    <p class="text-lg font-black text-slate-900 mt-1">{{ job()?.status }}</p>
                  </div>
                </div>
              } @else {
                <div class="mt-6 bg-amber-50 border border-amber-100 rounded-2xl p-5 flex items-start gap-3">
                  <i class="ti ti-alert-circle text-amber-600 text-xl mt-0.5"></i>
                  <div>
                    <p class="font-bold text-amber-900">No quote is linked yet.</p>
                    <p class="text-sm text-amber-700 mt-1">For inspection jobs, build and send the quote to move the job into customer approval.</p>
                  </div>
                </div>
              }
              @if (linkedServiceJobs().length > 0) {
                <div class="mt-6">
                  <p class="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-2">Linked Service Work</p>
                  <div class="space-y-2">
                    @for (service of linkedServiceJobs(); track service.id) {
                      <a [routerLink]="['/admin/dispatch/job', service.id]" class="flex items-center justify-between rounded-2xl border border-slate-100 bg-slate-50 px-4 py-3 no-underline hover:bg-sky-50">
                        <div>
                          <p class="text-sm font-bold text-slate-900">{{ service.id }} - {{ service.customer_name }}</p>
                          <p class="text-xs text-slate-500">{{ service.address }}</p>
                        </div>
                        <span class="text-xs font-bold text-[#1A6FA6]">{{ service.status }}</span>
                      </a>
                    }
                  </div>
                </div>
              }
              @if (inspectionNoServiceRequired()) {
                <div class="mt-6 rounded-2xl border border-slate-200 bg-slate-50 px-5 py-4">
                  <p class="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Inspection Closed Without Service</p>
                  <p class="text-sm font-semibold text-slate-800 mt-1">{{ job()?.inspection_service_remarks || 'No further service work required.' }}</p>
                </div>
              }
            </div>
          }

          <!-- OFFICE REVIEW TAB -->
          @if (activeTab() === 'office') {
            <div class="grid grid-cols-1 xl:grid-cols-[1.15fr_0.85fr] gap-5">
              <div class="bg-white rounded-3xl border border-slate-200 shadow-sm p-6">
                <div class="flex flex-wrap items-start justify-between gap-3 mb-4">
                  <div>
                    <h2 class="text-lg font-bold text-slate-900">Office Close-Out Checklist</h2>
                    <p class="text-sm text-slate-500 mt-1">Use this before closing the job so field evidence, safety, quote state, and follow-up are clear.</p>
                  </div>
                  <span class="px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest border"
                        [ngClass]="canCompleteJob() ? 'bg-emerald-50 text-emerald-700 border-emerald-100' : 'bg-amber-50 text-amber-700 border-amber-100'">
                    {{ completedOfficeChecklistCount() }} / {{ officeChecklist().length }} ready
                  </span>
                </div>
                <div class="space-y-3">
                  @for (item of officeChecklist(); track item.key) {
                    <div class="rounded-2xl border px-4 py-3 flex items-start gap-3"
                         [ngClass]="item.done ? 'bg-emerald-50 border-emerald-100' : (item.blocker ? 'bg-rose-50 border-rose-100' : 'bg-amber-50 border-amber-100')">
                      <span class="w-8 h-8 rounded-xl flex items-center justify-center shrink-0"
                            [ngClass]="item.done ? 'bg-white text-emerald-600' : (item.blocker ? 'bg-white text-rose-600' : 'bg-white text-amber-600')">
                        <i class="ti" [class]="item.done ? 'ti-check' : (item.blocker ? 'ti-alert-triangle' : 'ti-dots')"></i>
                      </span>
                      <div class="min-w-0 flex-1">
                        <div class="flex flex-wrap items-center justify-between gap-2">
                          <p class="text-sm font-bold" [ngClass]="item.done ? 'text-emerald-900' : (item.blocker ? 'text-rose-900' : 'text-amber-900')">{{ item.label }}</p>
                          <span class="text-[10px] font-black uppercase tracking-widest" [ngClass]="item.done ? 'text-emerald-700' : (item.blocker ? 'text-rose-700' : 'text-amber-700')">{{ item.done ? 'Ready' : (item.blocker ? 'Blocked' : 'Review') }}</span>
                        </div>
                        <p class="text-xs mt-1 leading-relaxed" [ngClass]="item.done ? 'text-emerald-800' : (item.blocker ? 'text-rose-800' : 'text-amber-800')">{{ item.detail }}</p>
                      </div>
                    </div>
                  }
                </div>
              </div>
              <div class="bg-white rounded-3xl border border-slate-200 shadow-sm p-6">
                <h2 class="text-lg font-bold text-slate-900 mb-4">Next Office Action</h2>
                <p class="text-sm text-slate-600 leading-relaxed">{{ officeNextAction() }}</p>
                <div class="mt-5 flex flex-wrap gap-3">
                  <button (click)="switchTab('timeline')" class="btn-open">Timeline</button>
                  @if (!isArchived()) { <a [routerLink]="quoteRoute()" [queryParams]="quoteQueryParams()" class="btn-open">Quote</a> }
                  @if (job()?.status === 'Ready for Office') {
                    <button (click)="markCompleted()" [disabled]="isSaving()" class="btn-open disabled:opacity-50 disabled:cursor-not-allowed">{{ officeBlockers().length ? 'Complete with Exception' : 'Mark Completed' }}</button>
                  }
                  @if ((job()?.status === 'Ready for Office' || job()?.status === 'Completed') && !isArchived() && !job()?.review_request?.email_sent && !job()?.review_request?.already_submitted) {
                    <button (click)="sendFeedbackRequest()" [disabled]="isSaving()" class="btn-open">Send Review Request</button>
                  }
                </div>
                @if (!canCompleteJob() && job()?.status === 'Ready for Office') {
                  <div class="mt-5 rounded-2xl border border-amber-100 bg-amber-50 px-4 py-3">
                    <p class="text-xs font-bold text-amber-900">Close-out is not ready yet.</p>
                    <p class="text-xs text-amber-800 mt-1">{{ firstOfficeBlocker() }}</p>
                  </div>
                }
              </div>
              @if (job()?.customer_feedback; as customerFeedback) {
                <section class="xl:col-span-2 rounded-3xl border shadow-sm p-6"
                         [ngClass]="customerFeedback.rating <= 3 ? 'bg-rose-50 border-rose-100' : 'bg-emerald-50 border-emerald-100'">
                  <div class="flex flex-col md:flex-row md:items-start justify-between gap-4">
                    <div class="min-w-0">
                      <p class="text-[10px] font-bold uppercase tracking-widest"
                         [ngClass]="customerFeedback.rating <= 3 ? 'text-rose-600' : 'text-emerald-600'">Customer Feedback</p>
                      <h2 class="text-lg font-bold text-slate-900 mt-1">Feedback received for this job</h2>
                      <p class="text-xs text-slate-500 mt-1">Submitted {{ formatDate(customerFeedback.submitted_at) }}</p>
                    </div>
                    <div class="flex items-center gap-1 shrink-0" [attr.aria-label]="customerFeedback.rating + ' out of 5 stars'">
                      @for (star of feedbackStars; track star) {
                        <i class="ti text-2xl leading-none"
                           [class.ti-star-filled]="star <= customerFeedback.rating"
                           [class.ti-star]="star > customerFeedback.rating"
                           [ngClass]="star <= customerFeedback.rating ? feedbackStarColour(customerFeedback.rating) : 'text-slate-300'"></i>
                      }
                      <span class="ml-2 text-sm font-bold text-slate-700">{{ customerFeedback.rating }}/5</span>
                    </div>
                  </div>
                  <div class="grid grid-cols-1 md:grid-cols-[minmax(0,1fr)_220px] gap-4 mt-5">
                    <div class="rounded-2xl bg-white/80 border border-white p-4">
                      <p class="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Customer comments</p>
                      <p class="text-sm text-slate-700 leading-relaxed mt-2 whitespace-pre-wrap">{{ customerFeedback.comments || 'No written comments were supplied.' }}</p>
                    </div>
                    <div class="rounded-2xl bg-white/80 border border-white p-4">
                      <p class="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Service area</p>
                      <p class="text-sm font-semibold text-slate-800 mt-2">{{ customerFeedback.service_category || 'Overall Service' }}</p>
                      <span class="inline-flex mt-3 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wide"
                            [ngClass]="customerFeedback.status === 'Needs Follow-up' ? 'bg-rose-100 text-rose-700' : 'bg-emerald-100 text-emerald-700'">
                        {{ customerFeedback.status }}
                      </span>
                    </div>
                  </div>
                </section>
              }
            </div>
          }

          <!-- TIMELINE TAB -->
          @if (activeTab() === 'timeline') {
            <div class="bg-white rounded-3xl border border-slate-200 shadow-sm p-6">
              <div class="flex flex-wrap items-start justify-between gap-3 mb-6">
                <div>
                  <h2 class="text-lg font-bold text-slate-900">Job Timeline</h2>
                  <p class="text-sm text-slate-500 mt-1">A single history of intake, dispatch, field activity, safety, quote movement, evidence, and close-out.</p>
                </div>
                <span class="px-3 py-1 rounded-full bg-slate-50 border border-slate-100 text-[10px] font-black uppercase tracking-widest text-slate-500">{{ jobTimeline().length }} events</span>
              </div>
              @if (job()?.scheduled_date) {
                <div class="mb-6 rounded-2xl border border-sky-100 bg-sky-50 px-4 py-3 flex items-center gap-3">
                  <span class="w-10 h-10 rounded-xl bg-white text-sky-600 border border-sky-100 flex items-center justify-center"><i class="ti ti-calendar-event text-lg"></i></span>
                  <div class="min-w-0 flex-1">
                  <p class="text-[10px] font-bold uppercase tracking-widest text-sky-600">Planned appointment</p>
                    <p class="text-sm font-bold text-slate-900 mt-0.5">{{ formatDate(job().scheduled_date) }}</p>
                    <p class="text-xs text-slate-500 mt-0.5">Assigned team: {{ workerNames() }}</p>
                  </div>
                  <span class="px-2 py-1 rounded-md bg-white border border-sky-100 text-[10px] font-bold text-sky-700">Auckland time</span>
                </div>
              }
              <div class="flex flex-wrap items-center justify-between gap-2 mb-4">
                <h3 class="text-sm font-bold text-slate-900">Activity history</h3>
                <span class="text-[10px] font-bold uppercase tracking-widest text-slate-400">Newest first &middot; Auckland time</span>
              </div>
              <div class="relative space-y-0 before:absolute before:left-5 before:top-5 before:bottom-5 before:w-px before:bg-slate-100">
                @for (event of jobTimeline(); track event.key) {
                  @if (event.showDateHeader) {
                    <div class="relative z-[2] ml-14 py-2">
                      <span class="inline-flex rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-slate-500">{{ event.dayLabel }}</span>
                    </div>
                  }
                  <div class="relative flex gap-4 pb-4">
                    <span class="relative z-[1] w-10 h-10 rounded-2xl flex items-center justify-center shrink-0" [ngClass]="event.iconClass">
                      <i class="ti" [class]="event.icon"></i>
                    </span>
                    <div class="min-w-0 flex-1 border-b border-slate-100 pb-4">
                      <div class="flex flex-wrap items-start justify-between gap-2">
                        <div class="min-w-0">
                          <p class="text-sm font-bold text-slate-900">{{ event.title }}</p>
                          <p class="text-xs text-slate-500 mt-1 leading-relaxed">{{ event.detail }}</p>
                        </div>
                        <span class="text-[11px] font-semibold text-slate-400 whitespace-nowrap">{{ event.date ? formatDate(event.date) : 'No date' }}</span>
                      </div>
                    </div>
                  </div>
                } @empty {
                  <div class="rounded-2xl border border-dashed border-slate-300 p-10 text-center">
                    <i class="ti ti-history text-[42px] text-slate-300"></i>
                    <p class="text-sm font-bold text-slate-900 mt-3">No timeline activity yet</p>
                  </div>
                }
              </div>
            </div>
          }

        }
      </div>

      @if (selectedPhoto()) {
        <div class="fixed inset-0 z-[100] bg-slate-950/90 backdrop-blur-sm flex flex-col" (click)="closePhotoViewer()">
          <div class="h-16 px-4 md:px-6 flex items-center justify-between gap-3 text-white border-b border-white/10 shrink-0" (click)="$event.stopPropagation()">
            <div class="min-w-0">
              <p class="text-[10px] uppercase tracking-widest text-white/45 font-bold">Job Photo</p>
              <h2 class="text-sm md:text-base font-semibold truncate">{{ selectedPhoto()?.caption || 'Site photo' }}</h2>
            </div>
            <div class="flex items-center gap-2 shrink-0">
              <span class="hidden sm:inline text-xs text-white/45 font-semibold">{{ photoViewerIndex()! + 1 }} / {{ photos().length }}</span>
              <button type="button" (click)="downloadSelectedPhoto()" [disabled]="downloadingPhotoId() === selectedPhoto()?.id" class="h-10 px-3 rounded-xl bg-white/10 hover:bg-white/15 text-white flex items-center gap-2 disabled:opacity-50">
                <i [class]="downloadingPhotoId() === selectedPhoto()?.id ? 'ti ti-loader animate-spin' : 'ti ti-download'"></i>
                <span class="hidden sm:inline">Download</span>
              </button>
              <button type="button" (click)="closePhotoViewer()" class="w-10 h-10 rounded-xl bg-white/10 hover:bg-white/15 text-white flex items-center justify-center">
                <i class="ti ti-x text-xl"></i>
              </button>
            </div>
          </div>

          <div class="relative flex-1 min-h-0 flex items-center justify-center p-4 md:p-8" (click)="$event.stopPropagation()">
            @if (photos().length > 1) {
              <button type="button" (click)="showPreviousPhoto()" class="absolute left-3 md:left-6 top-1/2 -translate-y-1/2 w-11 h-11 md:w-12 md:h-12 rounded-full bg-white/10 hover:bg-white/20 border border-white/10 text-white flex items-center justify-center shadow-lg">
                <i class="ti ti-chevron-left text-2xl"></i>
              </button>
              <button type="button" (click)="showNextPhoto()" class="absolute right-3 md:right-6 top-1/2 -translate-y-1/2 w-11 h-11 md:w-12 md:h-12 rounded-full bg-white/10 hover:bg-white/20 border border-white/10 text-white flex items-center justify-center shadow-lg">
                <i class="ti ti-chevron-right text-2xl"></i>
              </button>
            }
            <img [src]="selectedPhoto()?.photo_data" [alt]="selectedPhoto()?.caption || 'Site photo'" class="max-w-full max-h-full object-contain rounded-xl shadow-2xl bg-white/5">
          </div>

          <div class="px-4 md:px-6 py-4 border-t border-white/10 text-white bg-slate-950/70" (click)="$event.stopPropagation()">
            <div class="max-w-5xl mx-auto flex flex-col sm:flex-row sm:items-center justify-between gap-2">
              <div class="min-w-0">
                @if (selectedPhoto()?.caption) {
                  <p class="text-sm font-semibold truncate">{{ selectedPhoto()?.caption }}</p>
                }
                <p class="text-xs text-white/50">{{ selectedPhoto()?.uploader_name || 'Unknown uploader' }} - {{ formatDate(selectedPhoto()?.created_at) }}</p>
              </div>
              @if (photos().length > 1) {
                <div class="flex items-center gap-2 text-xs text-white/55 font-semibold">
                  <button type="button" (click)="showPreviousPhoto()" class="h-9 px-3 rounded-lg bg-white/10 text-white hover:bg-white/15 flex items-center gap-1">
                    <i class="ti ti-chevron-left"></i> Prev
                  </button>
                  <span>{{ photoViewerIndex()! + 1 }} of {{ photos().length }}</span>
                  <button type="button" (click)="showNextPhoto()" class="h-9 px-3 rounded-lg bg-white/10 text-white hover:bg-white/15 flex items-center gap-1">
                    Next <i class="ti ti-chevron-right"></i>
                  </button>
                </div>
              }
            </div>
          </div>
        </div>
      }
      @if (showNoServiceModal()) {
        <div class="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div class="bg-white rounded-3xl shadow-xl w-full max-w-lg border border-slate-100 overflow-hidden">
            <div class="p-5 border-b border-slate-100 flex items-center justify-between bg-slate-50/50">
              <div>
                <h2 class="text-lg font-bold text-slate-900">No Service Required</h2>
                <p class="text-xs text-slate-500 font-medium">Record why this accepted inspection does not need follow-up service work.</p>
              </div>
              <button (click)="closeNoServiceModal()" class="w-9 h-9 rounded-full hover:bg-slate-200 flex items-center justify-center text-slate-400">
                <i class="ti ti-x"></i>
              </button>
            </div>
            <div class="p-5">
              <label class="block">
                <span class="text-sm font-semibold text-slate-700 mb-1 block">Remarks *</span>
                <textarea [(ngModel)]="noServiceRemarks" rows="4" class="w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm outline-none focus:bg-white focus:border-[#1A6FA6] resize-none" placeholder="Example: inspection only, customer does not want work, issue resolved onsite..."></textarea>
              </label>
            </div>
            <div class="p-5 border-t border-slate-100 bg-slate-50/50 flex justify-end gap-3">
              <button (click)="closeNoServiceModal()" class="h-10 px-4 rounded-xl text-slate-600 text-sm font-bold">Cancel</button>
              <button (click)="saveNoServiceRequired()" [disabled]="isSaving() || noServiceRemarks.trim().length < 5" class="h-10 px-5 rounded-xl bg-slate-800 text-white text-sm font-bold disabled:opacity-50">
                @if (isSaving()) { Saving... } @else { Save Decision }
              </button>
            </div>
          </div>
        </div>
      }
    </div>
  `
})
export class AdminJobDetailComponent implements OnInit {
  api = inject(ApiService);
  route = inject(ActivatedRoute);
  router = inject(Router);
  jobStatusClass = jobStatusClass;

  job = signal<any>(null);
  photos = signal<any[]>([]);
  notes = signal<any[]>([]);
  linkedServiceJobs = signal<any[]>([]);
  isLoading = signal(true);
  activeTab = signal<'photos' | 'notes' | 'materials' | 'quote' | 'office' | 'timeline'>('photos');

  // Multi-Worker Management
  showManageWorkers = signal(false);
  allWorkers = signal<any[]>([]);
  fleetVehicles = signal<any[]>([]);
  dispatchCalendarEvents = signal<Record<string, any[]>>({});
  isDispatchCalendarLoading = signal(false);
  selectedWorkerIds = signal<number[]>([]);
  selectedFleetVehicleIds: Record<number, string> = {};
  isSaving = signal(false);
  readonly feedbackStars = [1, 2, 3, 4, 5];
  isSendingSafetyEmail = signal(false);
  isResolvingSafety = signal(false);
  photoViewerIndex = signal<number | null>(null);
  downloadingPhotoId = signal<number | null>(null);
  selectedPhoto = computed(() => {
    const index = this.photoViewerIndex();
    return index === null ? null : this.photos()[index] || null;
  });
  showScheduleModal = signal(false);
  showArchiveModal = signal(false);
  showNoServiceModal = signal(false);
  showPauseResolutionModal = signal(false);
  showCloseoutModal = signal(false);
  scheduleDate = '';
  scheduleTime = '';
  archiveRemarks = '';
  noServiceRemarks = '';
  pauseResolutionNotes = '';
  officeCloseoutNote = '';
  officeExceptionRemarks = '';
  materialReadinessStatuses = ['Estimated', 'Required', 'Ready', 'Used', 'Unavailable', 'Supplier Order Needed'];
  scheduleChangeCategories = ['Customer Unavailable', 'Parts Not Ready', 'Worker Unavailable', 'Emergency Priority Changed', 'Weather/Site Access', 'Other'];
  scheduleChangeCategory = 'Customer Unavailable';
  scheduleChangeRemarks = '';

  @HostListener('window:keydown', ['$event'])
  handlePhotoViewerKeys(event: KeyboardEvent) {
    if (!this.selectedPhoto()) return;
    if (event.key === 'Escape') {
      this.closePhotoViewer();
    } else if (event.key === 'ArrowLeft') {
      this.showPreviousPhoto();
    } else if (event.key === 'ArrowRight') {
      this.showNextPhoto();
    }
  }

  openPhotoViewer(index: number) {
    if (index < 0 || index >= this.photos().length) return;
    this.photoViewerIndex.set(index);
  }

  closePhotoViewer() {
    this.photoViewerIndex.set(null);
  }

  showPreviousPhoto() {
    const total = this.photos().length;
    const current = this.photoViewerIndex();
    if (!total || current === null) return;
    this.photoViewerIndex.set((current - 1 + total) % total);
  }

  showNextPhoto() {
    const total = this.photos().length;
    const current = this.photoViewerIndex();
    if (!total || current === null) return;
    this.photoViewerIndex.set((current + 1) % total);
  }

  downloadSelectedPhoto() {
    const photo = this.selectedPhoto();
    if (!photo?.id || this.downloadingPhotoId() !== null) return;
    this.downloadingPhotoId.set(Number(photo.id));
    this.api.downloadPhoto(photo.id).subscribe({
      next: blob => {
        this.downloadingPhotoId.set(null);
        this.savePhotoBlob(blob, `job-photo-${photo.id}`);
      },
      error: (err: any) => {
        this.downloadingPhotoId.set(null);
        showToast('error', err?.error?.message || 'Photo could not be downloaded.');
      }
    });
  }

  private savePhotoBlob(blob: Blob, baseName: string) {
    const extension = blob.type === 'image/png' ? 'png' : blob.type === 'image/webp' ? 'webp' : blob.type === 'image/gif' ? 'gif' : 'jpg';
    const objectUrl = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = objectUrl;
    link.download = `${baseName}.${extension}`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
  }

  customerContact() {
    return this.job()?.contacts?.find((c: any) => c.contact_type === 'Customer') || null;
  }

  latestSafetyStatus(): 'Accepted' | 'Declined' | '' {
    return this.job()?.latest_safety_acknowledgement?.status || (this.job()?.latest_safety_acknowledgement ? 'Accepted' : '');
  }

  isArchived(): boolean {
    return Number(this.job()?.is_archived || 0) === 1;
  }

  canManageArchive(): boolean {
    return ['Admin', 'Dispatcher', 'Manager'].includes(this.api.currentUser()?.role || '');
  }

  inspectionNoServiceRequired(): boolean {
    return this.job()?.type === 'Inspection' && this.job()?.inspection_service_decision === 'No Service Required';
  }

  inspectionServiceRequired(): boolean {
    const current = this.job();
    if (!current || current.type !== 'Inspection') return false;
    return current.inspection_service_decision === 'Service Required' || current.status === 'Approved' || current.quote?.status === 'Accepted';
  }

  inspectionNeedsServiceDecision(): boolean {
    const current = this.job();
    if (!current || current.type !== 'Inspection') return false;
    return this.inspectionServiceRequired() && this.linkedServiceJobs().length === 0 && !this.inspectionNoServiceRequired();
  }

  canScheduleJob(): boolean {
    if (this.isArchived()) return false;
    return !['In Progress', 'Paused', 'Ready for Office', 'Completed', 'Terminated'].includes(this.job()?.status || '');
  }

  nextFieldAction(): { label: string; status: string; icon: string; className: string } | null {
    if (this.isArchived()) return null;
    const status = this.job()?.status;
    if (status === 'Scheduled' || status === 'Approved') {
      return { label: 'Start Job', status: 'In Progress', icon: 'ti-player-play', className: 'bg-emerald-600 hover:bg-emerald-700' };
    }
    if (status === 'Paused') {
      return { label: 'Resume', status: 'In Progress', icon: 'ti-player-play', className: 'bg-emerald-600 hover:bg-emerald-700' };
    }
    if (status === 'In Progress') {
      return { label: 'Ready for Office', status: 'Ready for Office', icon: 'ti-clipboard-check', className: 'bg-sky-600 hover:bg-sky-700' };
    }
    return null;
  }

  openScheduleModal() {
    const scheduled = this.job()?.scheduled_date ? new Date(this.job().scheduled_date) : null;
    if (scheduled && !Number.isNaN(scheduled.getTime())) {
      this.scheduleDate = this.toDateInput(scheduled);
      this.scheduleTime = `${String(scheduled.getHours()).padStart(2, '0')}:${String(scheduled.getMinutes()).padStart(2, '0')}`;
    } else {
      this.scheduleDate = '';
      this.scheduleTime = '';
    }
    this.resetScheduleChangeReason();
    this.showScheduleModal.set(true);
  }

  closeScheduleModal() {
    if (this.isSaving()) return;
    this.showScheduleModal.set(false);
    this.resetScheduleChangeReason();
  }

  dispatchStats() {
    const workers = this.allWorkers();
    const available = workers.filter(worker => this.dispatchAvailability(worker).available).length;
    const appReady = workers.filter(worker => this.workerAppReady(worker)).length;
    const vehicleReady = workers.filter(worker => !!this.workerAssignedVehicle(worker.id)).length;
    return [
      { label: 'Available', value: `${available}/${workers.length}`, help: this.scheduleDate ? 'Not on approved leave that day' : 'Pick a date to check leave', className: 'text-emerald-700' },
      { label: 'App Ready', value: `${appReady}/${workers.length}`, help: 'Installed or recently seen', className: 'text-[#1A6FA6]' },
      { label: 'Vehicle Linked', value: `${vehicleReady}/${workers.length}`, help: 'Assigned vehicle found', className: 'text-slate-700' }
    ];
  }

  openArchiveModal() {
    if (this.isArchived() || !this.canManageArchive()) return;
    this.archiveRemarks = '';
    this.showArchiveModal.set(true);
  }

  closeArchiveModal() {
    if (this.isSaving()) return;
    this.showArchiveModal.set(false);
    this.archiveRemarks = '';
  }

  openNoServiceModal() {
    this.noServiceRemarks = this.job()?.inspection_service_remarks || '';
    this.showNoServiceModal.set(true);
  }

  closeNoServiceModal() {
    if (this.isSaving()) return;
    this.showNoServiceModal.set(false);
    this.noServiceRemarks = '';
  }

  openPauseResolutionModal() {
    if (this.job()?.status !== 'Paused' || this.isArchived()) return;
    this.pauseResolutionNotes = '';
    this.showPauseResolutionModal.set(true);
  }

  closePauseResolutionModal() {
    if (this.isSaving()) return;
    this.showPauseResolutionModal.set(false);
    this.pauseResolutionNotes = '';
  }

  openCloseoutModal() {
    if (this.isArchived() || this.job()?.status !== 'Ready for Office') return;
    this.officeCloseoutNote = this.job()?.office_closeout_note || '';
    this.officeExceptionRemarks = this.job()?.office_exception_remarks || '';
    this.showCloseoutModal.set(true);
  }

  closeCloseoutModal() {
    if (this.isSaving()) return;
    this.showCloseoutModal.set(false);
    this.officeCloseoutNote = '';
    this.officeExceptionRemarks = '';
  }

  private initScheduleForm() {
    const scheduled = this.job()?.scheduled_date ? new Date(this.job().scheduled_date) : this.nextDefaultScheduleDate();
    this.scheduleDate = this.toDateInput(scheduled);
    this.scheduleTime = `${String(scheduled.getHours()).padStart(2, '0')}:${String(scheduled.getMinutes()).padStart(2, '0')}`;
  }

  private nextDefaultScheduleDate(): Date {
    const date = new Date();
    date.setMinutes(0, 0, 0);
    date.setHours(date.getHours() + 1);
    return date;
  }

  saveSchedule() {
    const id = this.job()?.id;
    if (!id || !this.scheduleDate || !this.scheduleTime || this.isSaving()) return;
    this.saveSchedulePayload(`${this.scheduleDate} ${this.scheduleTime}:00`);
  }

  clearSchedule() {
    if (!this.job()?.id || this.isSaving()) return;
    this.saveSchedulePayload(null);
  }

  private saveSchedulePayload(scheduledDate: string | null) {
    const id = this.job()?.id;
    if (!id) return;
    if (this.scheduleReasonInvalid(scheduledDate)) {
      showToast('error', 'Add a schedule change reason before saving.');
      return;
    }
    this.isSaving.set(true);
    const payload: any = { id, scheduled_date: scheduledDate };
    if (this.scheduleChangeNeedsReason(scheduledDate)) {
      payload.schedule_change_category = this.scheduleChangeCategory;
      payload.schedule_change_remarks = this.scheduleChangeRemarks.trim();
    }
    this.api.scheduleJob(payload).subscribe({
      next: (res: any) => {
        this.isSaving.set(false);
        if (res.success) {
          showToast('success', scheduledDate ? 'Job scheduled.' : 'Job schedule cleared.');
          this.showScheduleModal.set(false);
          this.resetScheduleChangeReason();
          this.loadData(id.toString());
        } else {
          showToast('error', res.message || 'Schedule could not be updated.');
        }
      },
      error: (err: any) => {
        this.isSaving.set(false);
        showToast('error', err?.error?.message || 'Schedule could not be updated.');
      }
    });
  }

  scheduleChangeNeedsReason(nextScheduledDate: string | null): boolean {
    const current = this.job()?.scheduled_date || null;
    if (!current) return false;
    return this.scheduleKey(current) !== this.scheduleKey(nextScheduledDate);
  }

  scheduleReasonInvalid(nextScheduledDate: string | null): boolean {
    return this.scheduleChangeNeedsReason(nextScheduledDate) && (!this.scheduleChangeCategory || this.scheduleChangeRemarks.trim().length < 5);
  }

  private scheduleKey(value: string | null): string {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')} ${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
  }

  private resetScheduleChangeReason() {
    this.scheduleChangeCategory = this.scheduleChangeCategories[0];
    this.scheduleChangeRemarks = '';
  }

  workerNames(): string {
    const workers = this.job()?.assigned_workers;
    if (workers && workers.length > 0) {
      return workers.map((w: any) => w.name).join(', ');
    }
    return this.job()?.plumber || '—';
  }

  ngOnInit() {
    this.route.paramMap.subscribe(params => {
       const id = params.get('id');
       if (id) { this.loadData(id); }
    });
    this.loadAllWorkers();
    this.loadFleetVehicles();
  }

  loadAllWorkers() {
    this.api.getWorkers().subscribe({
      next: (res: any) => {
        if (res.success && res.data?.users) {
          this.allWorkers.set(res.data.users.filter((u: any) => u.is_active));
        }
      }
    });
  }

  loadFleetVehicles() {
    this.api.getFleetVehicles().subscribe({
      next: (res: any) => {
        if (res.success && res.data?.vehicles) {
          this.fleetVehicles.set(res.data.vehicles.filter((vehicle: any) => vehicle.is_active !== false));
        }
      }
    });
  }

  openManageWorkers() {
    if (this.isArchived()) {
      showToast('error', 'Archived jobs are view-only.');
      return;
    }
    const currentWorkers = this.job()?.assigned_workers || [];
    this.selectedWorkerIds.set(currentWorkers.map((w: any) => w.id));
    this.selectedFleetVehicleIds = {};
    this.initScheduleForm();
    this.resetScheduleChangeReason();
    this.loadDispatchCalendar();
    this.showManageWorkers.set(true);
  }

  closeManageWorkers() {
    this.selectedFleetVehicleIds = {};
    this.resetScheduleChangeReason();
    this.showManageWorkers.set(false);
  }

  toggleWorkerSelection(id: number) {
    this.selectedWorkerIds.update(ids => {
      if (ids.includes(id)) {
        delete this.selectedFleetVehicleIds[id];
        return ids.filter(i => i !== id);
      }
      return [...ids, id];
    });
  }

  isWorkerSelected(id: number): boolean {
    return this.selectedWorkerIds().includes(id);
  }

  workerName(userId: string | number): string {
    const worker = this.allWorkers().find((w: any) => Number(w.id) === Number(userId));
    return worker?.name || 'Plumber';
  }

  fleetVehicleLabel(vehicle: any): string {
    const rego = vehicle?.rego ? `${vehicle.rego} - ` : '';
    const current = vehicle?.name && vehicle.name !== 'Unassigned' ? ` (${vehicle.name})` : '';
    return `${rego}${vehicle?.vehicle || 'Vehicle'}${current}`;
  }

  loadDispatchCalendar() {
    if (!this.scheduleDate) {
      this.dispatchCalendarEvents.set({});
      return;
    }
    this.isDispatchCalendarLoading.set(true);
    this.api.getStaffCalendar({ from: this.scheduleDate, to: this.scheduleDate }).subscribe({
      next: (res: any) => {
        this.isDispatchCalendarLoading.set(false);
        this.dispatchCalendarEvents.set(res.success ? (res.data?.events || {}) : {});
      },
      error: () => {
        this.isDispatchCalendarLoading.set(false);
        this.dispatchCalendarEvents.set({});
      }
    });
  }

  sortedDispatchWorkers(): any[] {
    return [...this.allWorkers()].sort((a, b) => {
      const aSelected = this.isWorkerSelected(a.id) ? 1 : 0;
      const bSelected = this.isWorkerSelected(b.id) ? 1 : 0;
      if (aSelected !== bSelected) return bSelected - aSelected;
      return this.dispatchScore(b) - this.dispatchScore(a);
    });
  }

  workerCardClass(worker: any): string {
    if (this.isWorkerSelected(worker.id)) return 'border-[#2B9AD2] bg-blue-50/50';
    const availability = this.dispatchAvailability(worker);
    if (availability.onApprovedLeave) return 'border-rose-100 bg-rose-50/60 hover:border-rose-200';
    if (!this.workerAppReady(worker)) return 'border-amber-100 bg-amber-50/60 hover:border-amber-200';
    return 'border-slate-50 hover:border-slate-200 bg-slate-50/30';
  }

  dispatchWorkerHints(worker: any): Array<{ label: string; icon: string; className: string }> {
    const availability = this.dispatchAvailability(worker);
    const vehicle = this.workerAssignedVehicle(worker.id);
    const load = this.workerDayLoad(worker.id);
    const appReady = this.workerAppReady(worker);
    const locationFresh = this.workerLocationFresh(worker);
    const hints = [
      availability.onApprovedLeave
        ? { label: 'On approved leave', icon: 'ti-calendar-x', className: 'bg-rose-100 text-rose-700' }
        : availability.onPendingLeave
          ? { label: 'Pending leave', icon: 'ti-calendar-question', className: 'bg-amber-100 text-amber-700' }
          : { label: load > 0 ? `${load} item(s) scheduled` : 'Clear schedule', icon: load > 0 ? 'ti-calendar-event' : 'ti-calendar-check', className: load > 0 ? 'bg-sky-100 text-sky-700' : 'bg-emerald-100 text-emerald-700' },
      appReady
        ? { label: worker.app_status?.label || 'App ready', icon: 'ti-device-mobile-check', className: 'bg-emerald-100 text-emerald-700' }
        : { label: worker.app_status?.label || 'App attention', icon: 'ti-device-mobile-exclamation', className: 'bg-amber-100 text-amber-700' },
      vehicle
        ? { label: vehicle.rego || vehicle.vehicle || 'Vehicle assigned', icon: 'ti-truck', className: 'bg-slate-100 text-slate-700' }
        : { label: 'No vehicle linked', icon: 'ti-car-off', className: 'bg-slate-100 text-slate-500' }
    ];
    if (locationFresh) {
      hints.push({ label: 'Location recent', icon: 'ti-map-pin-check', className: 'bg-cyan-100 text-cyan-700' });
    }
    return hints;
  }

  private dispatchScore(worker: any): number {
    const availability = this.dispatchAvailability(worker);
    let score = 0;
    if (availability.onApprovedLeave) score -= 100;
    if (availability.onPendingLeave) score -= 20;
    if (this.workerAppReady(worker)) score += 25;
    if (this.workerAssignedVehicle(worker.id)) score += 15;
    if (this.workerLocationFresh(worker)) score += 8;
    score -= this.workerDayLoad(worker.id) * 8;
    return score;
  }

  private dispatchAvailability(worker: any): { available: boolean; onApprovedLeave: boolean; onPendingLeave: boolean } {
    const events = this.workerCalendarEvents(worker.id);
    const leaveEvents = events.filter(event => event.type === 'leave');
    const onApprovedLeave = leaveEvents.some(event => event.status === 'Approved');
    const onPendingLeave = leaveEvents.some(event => event.status === 'Pending');
    return { available: !onApprovedLeave, onApprovedLeave, onPendingLeave };
  }

  private workerCalendarEvents(workerId: string | number): any[] {
    if (!this.scheduleDate) return [];
    return this.dispatchCalendarEvents()[`${workerId}|${this.scheduleDate}`] || [];
  }

  private workerDayLoad(workerId: string | number): number {
    return this.workerCalendarEvents(workerId)
      .filter(event => event.type === 'job' || event.type === 'project' || event.type === 'shift')
      .length;
  }

  private workerAppReady(worker: any): boolean {
    const status = worker.app_status?.status || '';
    return ['installed', 'browser_only'].includes(status);
  }

  private workerLocationFresh(worker: any): boolean {
    const last = worker.app_status?.last_location_at;
    if (!last) return false;
    const time = new Date(last).getTime();
    if (Number.isNaN(time)) return false;
    return Date.now() - time < 1000 * 60 * 60 * 24;
  }

  private workerAssignedVehicle(workerId: string | number): any | null {
    return this.fleetVehicles().find((vehicle: any) => Number(vehicle.assigned_user_id || 0) === Number(workerId)) || null;
  }

  saveAssignments() {
    const id = this.job()?.id;
    if (!id || this.isSaving()) return;
    if (this.isArchived()) {
      showToast('error', 'Archived jobs are view-only.');
      return;
    }
    if (this.selectedWorkerIds().length > 0 && (!this.scheduleDate || !this.scheduleTime)) {
      showToast('error', 'Set a schedule date and time before assigning workers.');
      return;
    }
    const nextScheduledDate = this.selectedWorkerIds().length > 0 ? `${this.scheduleDate} ${this.scheduleTime}:00` : null;
    if (this.scheduleReasonInvalid(nextScheduledDate)) {
      showToast('error', 'Add a schedule change reason before updating the team.');
      return;
    }
    
    this.isSaving.set(true);
    const payload: any = {
      reference_id: id,
      model_type: 'Job',
      user_ids: this.selectedWorkerIds(),
      scheduled_date: nextScheduledDate
    };
    if (this.scheduleChangeNeedsReason(nextScheduledDate)) {
      payload.schedule_change_category = this.scheduleChangeCategory;
      payload.schedule_change_remarks = this.scheduleChangeRemarks.trim();
    }

    this.api.assignWorker(payload).subscribe({
      next: (res: any) => {
        if (res.success) {
          this.saveOptionalFleetAssignments(id, () => {
            this.isSaving.set(false);
            showToast('success', 'Team assignment updated successfully');
            this.closeManageWorkers();
            this.loadData(id.toString());
            this.loadFleetVehicles();
          });
        } else {
          this.isSaving.set(false);
          showToast('error', res.message || 'Failed to update team');
        }
      },
      error: () => {
        this.isSaving.set(false);
        showToast('error', 'Network error updating team');
      }
    });
  }

  private saveOptionalFleetAssignments(jobId: string | number, onComplete: () => void) {
    const requests = this.selectedWorkerIds()
      .map(workerId => ({
        workerId,
        vehicleId: Number(this.selectedFleetVehicleIds[workerId] || 0)
      }))
      .filter(item => item.vehicleId > 0)
      .map(item => this.api.assignFleetVehicle({
        id: item.vehicleId,
        assigned_user_id: item.workerId,
        name: this.workerName(item.workerId),
        status: 'Active on Job',
        job: String(jobId),
        location: this.job()?.address || this.job()?.job_address || 'Field',
        notes: `Assigned during job team update ${jobId}`
      }));

    if (requests.length === 0) {
      onComplete();
      return;
    }

    forkJoin(requests).subscribe({
      next: () => onComplete(),
      error: (err: any) => {
        this.isSaving.set(false);
        showToast('error', err?.error?.message || 'Team updated, but fleet assignment failed.');
      }
    });
  }

  sendSafetyDeclineEmail() {
    const ack = this.job()?.latest_safety_acknowledgement;
    if (!ack?.id || this.isSendingSafetyEmail() || this.isArchived()) return;

    this.isSendingSafetyEmail.set(true);
    this.api.sendSafetyDeclineEmail(ack.id).subscribe({
      next: (res: any) => {
        this.isSendingSafetyEmail.set(false);
        if (res.success) {
          showToast('success', 'Safety concern emailed to customer.');
          this.loadData(this.job().id.toString());
        } else {
          showToast('error', res.message || 'Email could not be sent.');
        }
      },
      error: (err: any) => {
        this.isSendingSafetyEmail.set(false);
        showToast('error', err?.error?.message || 'Email could not be sent.');
      }
    });
  }

  resolveSafetyConcern() {
    const ack = this.job()?.latest_safety_acknowledgement;
    if (!ack?.id || ack.resolved_at || this.isResolvingSafety() || this.isArchived()) return;

    const notes = window.prompt('Resolution notes for this safety concern:', '');
    if (notes === null) return;

    this.isResolvingSafety.set(true);
    this.api.resolveSafetyConcern({ acknowledgement_id: ack.id, resolution_notes: notes.trim() }).subscribe({
      next: (res: any) => {
        this.isResolvingSafety.set(false);
        if (res.success) {
          showToast('success', 'Safety concern marked as resolved.');
          this.loadData(this.job().id.toString());
        } else {
          showToast('error', res.message || 'Safety concern could not be resolved.');
        }
      },
      error: (err: any) => {
        this.isResolvingSafety.set(false);
        showToast('error', err?.error?.message || 'Safety concern could not be resolved.');
      }
    });
  }

  loadData(id: string) {
    this.isLoading.set(true);
    
    this.api.getJob(id).subscribe({
      next: (jobRes: any) => {
        if (jobRes.success) {
          this.job.set(jobRes.data);
          this.loadLinkedServiceJobs();
          this.loadTabData(this.activeTab());
        } else {
          showToast('error', 'Job not found');
          this.isLoading.set(false);
        }
      },
      error: () => this.isLoading.set(false)
    });
  }

  switchTab(tab: 'photos' | 'notes' | 'materials' | 'quote' | 'office' | 'timeline') {
    this.activeTab.set(tab);
    this.loadTabData(tab);
  }

  loadTabData(tab: 'photos' | 'notes' | 'materials' | 'quote' | 'office' | 'timeline') {
    const id = this.job()?.id;
    if (!id) return;
    if (tab === 'materials' || tab === 'quote') return;
    if (tab === 'office' || tab === 'timeline') {
      this.isLoading.set(true);
      let pending = 2;
      const done = () => {
        pending--;
        if (pending === 0) this.isLoading.set(false);
      };
      this.api.getPhotos(id).subscribe({ next: (r: any) => { if (r.success) this.photos.set(r.data?.photos || []); done(); }, error: () => done() });
      this.api.getNotes(id).subscribe({ next: (r: any) => { if (r.success) this.notes.set(r.data?.notes || []); done(); }, error: () => done() });
      return;
    }
    this.isLoading.set(true);
    if (tab === 'photos') {
      this.api.getPhotos(id).subscribe({
        next: (r: any) => { if (r.success) this.photos.set(r.data?.photos || []); this.isLoading.set(false); },
        error: () => this.isLoading.set(false)
      });
    } else if (tab === 'notes') {
      this.api.getNotes(id).subscribe({
        next: (r: any) => { if (r.success) this.notes.set(r.data?.notes || []); this.isLoading.set(false); },
        error: () => this.isLoading.set(false)
      });
    }
  }

  formatDate(dateString: string) {
    if (!dateString) return '';
    return new Date(dateString).toLocaleString('en-NZ', {
      timeZone: 'Pacific/Auckland',
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZoneName: 'short'
    });
  }

  feedbackStarColour(rating: number): string {
    if (Number(rating) <= 2) return 'text-red-500';
    if (Number(rating) === 3) return 'text-amber-500';
    return 'text-emerald-500';
  }

  private toDateInput(date: Date): string {
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
  }

  loadLinkedServiceJobs() {
    const current = this.job();
    if (!current || current.type !== 'Inspection') {
      this.linkedServiceJobs.set([]);
      return;
    }
    this.api.getJobs().subscribe({
      next: (res: any) => {
        if (res.success && res.data?.jobs) {
          this.linkedServiceJobs.set(res.data.jobs.filter((j: any) => j.linked_inspection_id === current.id));
        }
      },
      error: () => this.linkedServiceJobs.set([])
    });
  }

  createServiceFromInspection() {
    const current = this.job();
    if (!current || current.type !== 'Inspection' || this.isSaving() || this.isArchived()) return;
    if (this.inspectionNoServiceRequired()) {
      showToast('error', 'This inspection is marked as no service required.');
      return;
    }
    if (this.linkedServiceJobs().length > 0) {
      showToast('error', 'A linked service job already exists.');
      return;
    }
    if (typeof window !== 'undefined' && !window.confirm('Create a service job linked to this inspection?')) return;

    this.isSaving.set(true);
    const customer = this.customerContact();
    const payload = {
      type: 'Service',
      priority: 'Routine',
      customer_name: current.customer_name || customer?.name || 'Customer',
      address: current.address || '',
      description: `Service work from inspection ${current.id}${current.description ? ': ' + current.description : ''}`,
      linked_inspection_id: current.id,
      contacts: current.contacts?.length ? current.contacts.map((c: any) => ({
        name: c.name,
        phone: c.phone,
        email: c.email,
        type: c.contact_type || c.type || 'Customer'
      })) : [{
        name: customer?.name || current.customer_name || 'Customer',
        phone: customer?.phone || '',
        email: customer?.email || '',
        type: 'Customer'
      }]
    };

    this.api.createJob(payload).subscribe({
      next: (res: any) => {
        this.isSaving.set(false);
        if (res.success) {
          showToast('success', 'Linked service job created.');
          this.loadData(current.id.toString());
        } else {
          showToast('error', res.message || 'Failed to create linked service job.');
        }
      },
      error: () => {
        this.isSaving.set(false);
        showToast('error', 'Network error creating linked service job.');
      }
    });
  }

  backRoute(): string {
    const status = this.job()?.status;
    if (status === 'Unassigned') return '/admin/dispatch/unassigned';
    if (status === 'Ready for Office' || status === 'Completed') return '/admin/dispatch/ready';
    return '/admin/dispatch/allocated';
  }

  quoteRoute(): any[] {
    const quoteId = this.job()?.quote?.id;
    return quoteId ? ['/admin/financials/quotes', quoteId] : ['/admin/financials/quotes/new'];
  }

  quoteQueryParams(): any {
    return this.job()?.quote?.id ? null : { job_id: this.job()?.id };
  }

  materialPrice(mat: any): number {
    return parseFloat(mat?.sell_price ?? mat?.cost ?? 0) || 0;
  }

  materialLineTotal(mat: any): number {
    return (parseFloat(mat?.quantity ?? 0) || 0) * this.materialPrice(mat);
  }

  materialsTotal(): number {
    return (this.job()?.materials || []).reduce((sum: number, mat: any) => sum + this.materialLineTotal(mat), 0);
  }

  officeNextAction(): string {
    const current = this.job();
    if (!current) return 'Load the job record to review the next action.';
    if (this.latestSafetyStatus() === 'Declined' && !current.latest_safety_acknowledgement?.resolved_at) return 'Resolve the declined safety acknowledgement before closing this job.';
    if (current.status === 'Unassigned') return 'Assign a plumber so the field workflow can begin.';
    if (current.status === 'Quote Sent') return 'Wait for customer approval, then schedule the service job or move the approved work forward.';
    if (current.status === 'Approved') return 'Customer has approved the quote. Confirm scheduling and field assignment before work starts.';
    if (current.status === 'In Progress') return 'Field work is active. Review photos, notes, and safety details as they arrive.';
    if (current.status === 'Paused') return 'This job has been paused by the field team. Review notes and wait for the worker to resume the job.';
    if (current.status === 'Ready for Office') return this.canCompleteJob() ? 'Close-out evidence is ready. You can mark this job completed.' : 'Complete the close-out checklist before marking this job completed.';
    if (current.status === 'Completed') return 'This job is closed. Confirm the customer feedback request has been sent or resend it if needed.';
    return 'Review the job evidence and progress it to the next office stage.';
  }

  officeChecklist(): Array<{ key: string; label: string; detail: string; done: boolean; blocker: boolean }> {
    const current = this.job();
    if (!current) return [];
    const safety = current.latest_safety_acknowledgement;
    const safetyDeclined = this.latestSafetyStatus() === 'Declined';
    const safetyResolved = !!safety?.resolved_at;
    const isInspection = current.type === 'Inspection';
    const isEmergency = current.type === 'Emergency' || current.priority === 'Urgent';
    const quoteStatus = current.quote?.status || '';
    const hasAcceptedQuote = quoteStatus === 'Accepted' || current.status === 'Approved' || this.linkedServiceJobs().length > 0;
    const noServiceRequired = this.inspectionNoServiceRequired();

    return [
      {
        key: 'safety',
        label: 'Safety is recorded and clear',
        detail: safety
          ? (safetyDeclined ? (safetyResolved ? 'Safety concern was declined and resolved by office.' : 'Safety concern is still unresolved.') : `Accepted by ${safety.worker_name || 'worker'}.`)
          : 'No safety acknowledgement has been recorded for this job.',
        done: !!safety && (!safetyDeclined || safetyResolved),
        blocker: !safety || (safetyDeclined && !safetyResolved)
      },
      {
        key: 'photos',
        label: 'Field photos reviewed',
        detail: this.photos().length > 0 ? `${this.photos().length} photo(s) uploaded for office review.` : 'No field photos are attached yet.',
        done: this.photos().length > 0,
        blocker: isEmergency || current.status === 'Ready for Office'
      },
      {
        key: 'notes',
        label: 'Worker notes reviewed',
        detail: this.notes().length > 0 ? `${this.notes().length} note(s) available from the field.` : 'No worker note has been added yet.',
        done: this.notes().length > 0,
        blocker: false
      },
      {
        key: 'materials',
        label: 'Materials checked',
        detail: this.materialsChecklistDetail(current),
        done: (current.materials || []).length > 0 || isInspection,
        blocker: this.materialsBlockingCount(current) > 0
      },
      {
        key: 'quote',
        label: 'Quote path is clear',
        detail: current.quote
          ? `Quote ${current.quote.quote_no || ''} is ${quoteStatus || 'Draft'}.`
          : (isInspection ? 'Inspection has no linked quote yet.' : 'No quote is linked. This is fine when the job does not require one.'),
        done: !isInspection || !!current.quote,
        blocker: isInspection && !current.quote
      },
      {
        key: 'inspection-service',
        label: 'Inspection to service handoff',
        detail: isInspection
          ? (noServiceRequired ? `No service required: ${current.inspection_service_remarks || 'remarks recorded.'}` : (hasAcceptedQuote ? (this.linkedServiceJobs().length > 0 ? 'Linked service job has been created.' : 'Quote is accepted; create linked service work or record no service required.') : 'Inspection quote has not been accepted yet.'))
          : (current.linked_inspection_id ? `Service job is linked to inspection ${current.linked_inspection_id}.` : 'Standard service job.'),
        done: !isInspection || !['Approved'].includes(current.status) || this.linkedServiceJobs().length > 0 || noServiceRequired,
        blocker: isInspection && current.status === 'Approved' && this.linkedServiceJobs().length === 0 && !noServiceRequired
      }
    ];
  }

  completedOfficeChecklistCount(): number {
    return this.officeChecklist().filter(item => item.done).length;
  }

  canCompleteJob(): boolean {
    const current = this.job();
    if (!current || this.isArchived()) return false;
    if (current.status !== 'Ready for Office') return true;
    return this.officeChecklist().every(item => item.done || !item.blocker);
  }

  officeBlockers(): any[] {
    return this.officeChecklist().filter(item => !item.done && item.blocker);
  }

  firstOfficeBlocker(): string {
    const blocker = this.officeBlockers()[0];
    return blocker ? blocker.detail : 'Review the remaining checklist items before completing.';
  }

  closeoutInvalid(): boolean {
    if (this.officeCloseoutNote.trim().length < 5) return true;
    return this.officeBlockers().length > 0 && this.officeExceptionRemarks.trim().length < 5;
  }

  materialsBlockingCount(current: any = this.job()): number {
    return (current?.materials || []).filter((mat: any) => ['Required', 'Unavailable', 'Supplier Order Needed'].includes(mat.readiness_status || '')).length;
  }

  materialsChecklistDetail(current: any): string {
    const materials = current.materials || [];
    if (!materials.length) return 'No materials were logged. Confirm this is correct for labour-only or inspection work.';
    const blocking = this.materialsBlockingCount(current);
    const total = this.materialsTotal().toLocaleString('en-NZ', { style: 'currency', currency: 'NZD' });
    if (blocking > 0) return `${blocking} material item(s) still need parts action before close-out. Total ${total}.`;
    return `${materials.length} material item(s), total ${total}. Parts readiness is clear.`;
  }

  jobTimeline(): any[] {
    const current = this.job();
    if (!current) return [];
    const events: any[] = [];

    const add = (key: string, title: string, detail: string, date: any, icon: string, iconClass: string) => {
      if (!date && key !== 'created') return;
      events.push({ key, title, detail, date, icon, iconClass });
    };

    const scheduleHistory = [...(current.schedule_history || [])].sort((a: any, b: any) => new Date(a.changed_at || 0).getTime() - new Date(b.changed_at || 0).getTime());
    const initialAppointment = scheduleHistory[0]?.old_scheduled_date || (scheduleHistory.length === 0 ? current.scheduled_date : null);
    const creationDetail = `${current.customer_name || 'Customer'} - ${current.address || 'No address recorded'}${initialAppointment ? `. Initial appointment: ${this.formatDate(initialAppointment)}` : ''}`;
    add('created', 'Job created', creationDetail, current.created_at, 'ti-inbox', 'bg-slate-50 text-slate-500');
    for (const change of current.schedule_history || []) {
      add(`schedule-change-${change.id}`, 'Schedule changed', this.scheduleHistoryDetail(change), change.changed_at, 'ti-calendar-time', 'bg-amber-50 text-amber-600');
    }
    for (const worker of current.assigned_workers || []) {
      add(`assigned-${worker.id}`, 'Worker assigned', worker.name || 'Worker assigned to job.', worker.assigned_at, 'ti-user-plus', 'bg-[#EAF5FA] text-[#1A6FA6]');
    }

    const workSessionEvents = current.work_session_events || [];
    const workSessions = current.work_sessions || [];
    if (workSessionEvents.length) {
      for (const event of workSessionEvents) {
        const workerName = event.worker_name || 'Worker';
        const totalHours = (Number(event.total_seconds || 0) / 3600).toFixed(2);
        if (event.event_type === 'started') {
          add(`worker-event-${event.id}`, `${workerName} started timing`, 'Individual worker timer started.', event.event_at, 'ti-player-play', 'bg-emerald-50 text-emerald-600');
        } else if (event.event_type === 'paused') {
          add(`worker-event-${event.id}`, `${workerName} paused timing`, `${event.category || 'Paused'}${event.remarks ? ': ' + event.remarks : ''}.`, event.event_at, 'ti-player-pause', 'bg-orange-50 text-orange-600');
        } else if (event.event_type === 'resumed') {
          add(`worker-event-${event.id}`, `${workerName} resumed timing`, event.remarks || 'Individual worker timer resumed.', event.event_at, 'ti-player-play', 'bg-emerald-50 text-emerald-600');
        } else if (event.event_type === 'stopped') {
          add(`worker-event-${event.id}`, `${workerName} stopped timing`, `${totalHours} hour(s) recorded for this worker.`, event.event_at, 'ti-player-stop', 'bg-slate-100 text-slate-600');
        }
      }
    }
    if (workSessions.length) {
      const recordedEvents = new Set(workSessionEvents.map((event: any) => `${event.session_id}-${event.event_type}`));
      for (const session of workSessions) {
        const workerName = session.worker_name || 'Worker';
        if (!recordedEvents.has(`${session.id}-started`)) {
          add(`worker-start-${session.id}`, `${workerName} started timing`, 'Individual worker timer started.', session.started_at, 'ti-player-play', 'bg-emerald-50 text-emerald-600');
        }
        if (session.paused_at && !recordedEvents.has(`${session.id}-paused`)) {
          add(`worker-pause-${session.id}`, `${workerName} paused timing`, `${session.pause_category || 'Paused'}${session.pause_remarks ? ': ' + session.pause_remarks : ''}.`, session.paused_at, 'ti-player-pause', 'bg-orange-50 text-orange-600');
        }
        if (!recordedEvents.has(`${session.id}-resumed`) && session.active_started_at && session.started_at && new Date(session.active_started_at).getTime() > new Date(session.started_at).getTime() + 1000) {
          add(`worker-resume-${session.id}`, `${workerName} resumed timing`, session.resume_remarks || 'Individual worker timer resumed.', session.active_started_at, 'ti-player-play', 'bg-emerald-50 text-emerald-600');
        }
        if (session.stopped_at && !recordedEvents.has(`${session.id}-stopped`)) {
          const hours = (Number(session.total_seconds || 0) / 3600).toFixed(2);
          add(`worker-stop-${session.id}`, `${workerName} stopped timing`, `${hours} hour(s) recorded for this worker.`, session.stopped_at, 'ti-player-stop', 'bg-slate-100 text-slate-600');
        }
      }
    }
    if (!workSessionEvents.length && !workSessions.length) {
      if (current.started_at) add('started', 'Field work started', 'Worker started the job from mobile.', current.started_at, 'ti-player-play', 'bg-emerald-50 text-emerald-600');
      if (current.paused_at || current.pause_started_at) add('paused', 'Job paused', this.pauseReasonText(current), current.pause_started_at || current.paused_at, 'ti-player-pause', 'bg-orange-50 text-orange-600');
      if (current.pause_resolved_at) add('pause-resolved', 'Pause resolved', current.pause_resolution_notes || 'Work was resumed.', current.pause_resolved_at, 'ti-player-play', 'bg-emerald-50 text-emerald-600');
    }
    if (current.completed_at) {
      const legacyCompleted = current.status === 'Completed' && !current.office_closed_at;
      add('field-complete', legacyCompleted ? 'Job completed' : 'Field work ready for office', legacyCompleted ? 'Job completion was recorded.' : 'The last worker stopped timing and field work moved to office review.', current.completed_at, 'ti-flag-check', 'bg-sky-50 text-sky-600');
    }
    if (current.office_closed_at || current.office_closeout_note) {
      const detail = `${current.office_closeout_note || 'Office close-out recorded.'}${current.office_exception_remarks ? ' Exception: ' + current.office_exception_remarks : ''}`;
      add('office-closeout', 'Job completed by office', detail, current.office_closed_at || current.completed_at, 'ti-clipboard-check', 'bg-emerald-50 text-emerald-600');
    }

    for (const ack of current.safety_acknowledgements || []) {
      const declined = ack.status === 'Declined';
      add(`safety-${ack.id}`, declined ? 'Safety declined' : 'Safety accepted', declined ? (ack.decline_reason || 'Safety concern recorded.') : `Accepted by ${ack.worker_name || 'worker'}.`, ack.declined_at || ack.accepted_at, declined ? 'ti-shield-x' : 'ti-shield-check', declined ? 'bg-rose-50 text-rose-600' : 'bg-emerald-50 text-emerald-600');
      if (ack.resolved_at) add(`safety-resolved-${ack.id}`, 'Safety concern resolved', ack.resolution_notes || 'Resolved by office.', ack.resolved_at, 'ti-check', 'bg-emerald-50 text-emerald-600');
    }

    const photoGroups = new Map<string, any[]>();
    for (const photo of this.photos()) {
      const timestamp = new Date(photo.created_at || 0);
      const minute = Number.isNaN(timestamp.getTime()) ? String(photo.created_at || '') : timestamp.toISOString().slice(0, 16);
      const groupKey = `${photo.uploader_name || 'worker'}-${minute}`;
      photoGroups.set(groupKey, [...(photoGroups.get(groupKey) || []), photo]);
    }
    for (const [groupKey, photos] of photoGroups) {
      const uploader = photos[0]?.uploader_name || 'worker';
      const captions = photos.map(photo => photo.caption).filter(Boolean);
      const detail = photos.length === 1
        ? (captions[0] || `Uploaded by ${uploader}.`)
        : `${photos.length} photos uploaded by ${uploader}${captions.length ? ': ' + captions.join(', ') : '.'}`;
      add(`photos-${groupKey}`, photos.length === 1 ? 'Photo uploaded' : `${photos.length} photos uploaded`, detail, photos[0]?.created_at, 'ti-camera', 'bg-violet-50 text-violet-600');
    }
    for (const note of this.notes()) {
      add(`note-${note.id}`, 'Note added', note.content || `Added by ${note.author_name || 'worker'}.`, note.created_at, 'ti-notes', 'bg-amber-50 text-amber-600');
    }
    for (const mat of current.materials || []) {
      add(`material-${mat.id}`, 'Material logged', `${mat.item_name || 'Material'} x ${mat.quantity || 0} ${mat.unit || 'EA'} - ${mat.readiness_status || 'Used'}${mat.readiness_note ? ': ' + mat.readiness_note : ''}.`, mat.created_at, 'ti-box', 'bg-slate-50 text-slate-600');
    }
    if (current.quote) {
      const quoteLabel = current.quote.quote_no || 'Quote linked to this job';
      add(`quote-created-${current.quote.id}`, 'Quote created', quoteLabel, current.quote.created_at, 'ti-file-dollar', 'bg-cyan-50 text-cyan-700');
      add(`quote-sent-${current.quote.id}`, 'Quote sent', quoteLabel, current.quote.sent_at, 'ti-send', 'bg-sky-50 text-sky-600');
      add(`quote-accepted-${current.quote.id}`, 'Quote accepted', quoteLabel, current.quote.accepted_at, 'ti-circle-check', 'bg-emerald-50 text-emerald-600');
      add(`quote-declined-${current.quote.id}`, 'Quote declined', quoteLabel, current.quote.declined_at, 'ti-circle-x', 'bg-rose-50 text-rose-600');
    }
    for (const service of this.linkedServiceJobs()) {
      add(`service-${service.id}`, 'Linked service job created', `${service.id} - ${service.status || 'Service work'}.`, service.created_at, 'ti-tool', 'bg-emerald-50 text-emerald-600');
    }
    if (current.inspection_service_decision === 'No Service Required') {
      add('inspection-no-service', 'No service required', current.inspection_service_remarks || 'Inspection closed without follow-up service work.', current.inspection_service_decided_at, 'ti-circle-off', 'bg-slate-100 text-slate-600');
    }
    if (current.customer_feedback?.submitted_at) {
      const customerFeedback = current.customer_feedback;
      const detail = `${customerFeedback.rating}/5${customerFeedback.service_category ? ' - ' + customerFeedback.service_category : ''}${customerFeedback.comments ? ': ' + customerFeedback.comments : '.'}`;
      add(
        'customer-feedback',
        'Customer feedback received',
        detail,
        customerFeedback.submitted_at,
        'ti-message-star',
        customerFeedback.rating <= 3 ? 'bg-rose-50 text-rose-600' : 'bg-emerald-50 text-emerald-600'
      );
    }
    if (current.is_archived) {
      add('archived', 'Job archived', current.archive_remarks || 'Archived by office.', current.archived_at, 'ti-archive', 'bg-slate-100 text-slate-600');
    }

    const sorted = events.sort((a, b) => new Date(b.date || 0).getTime() - new Date(a.date || 0).getTime());
    let previousDay = '';
    return sorted.map(event => {
      const dayKey = this.aucklandDayKey(event.date);
      const showDateHeader = dayKey !== previousDay;
      previousDay = dayKey;
      return { ...event, showDateHeader, dayLabel: this.formatTimelineDay(event.date) };
    });
  }

  private aucklandDayKey(dateString: string): string {
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return 'unknown';
    return new Intl.DateTimeFormat('en-CA', {
      timeZone: 'Pacific/Auckland', year: 'numeric', month: '2-digit', day: '2-digit'
    }).format(date);
  }

  formatTimelineDay(dateString: string): string {
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return 'Date unavailable';
    return new Intl.DateTimeFormat('en-NZ', {
      timeZone: 'Pacific/Auckland', weekday: 'long', day: 'numeric', month: 'long', year: 'numeric'
    }).format(date);
  }

  jobControlStageLabel(): string {
    const status = this.job()?.status || 'Unknown';
    if (status === 'Unassigned') return 'Intake';
    if (status === 'Scheduled' || status === 'Approved') return 'Dispatch';
    if (status === 'In Progress') return 'In Field';
    if (status === 'Paused') return 'Blocked';
    if (status === 'Quote Sent') return 'Customer Approval';
    if (status === 'Ready for Office') return 'Office Review';
    if (status === 'Completed') return 'Closed';
    if (status === 'Terminated') return 'Terminated';
    return status;
  }

  jobControlActionLabel(): string {
    const current = this.job();
    if (!current) return 'Load the job record to see the next action.';
    if (this.latestSafetyStatus() === 'Declined') return 'Safety was declined. Resolve the concern before moving this job forward.';
    if (current.status === 'Unassigned') return 'Allocate a plumber and schedule attendance.';
    if (current.status === 'Scheduled') return 'Worker should start shift, accept safety, then start the job.';
    if (current.status === 'Approved') return current.scheduled_date ? 'Approved work is scheduled. Worker can start when ready.' : 'Customer approved the quote. Schedule and assign the service work.';
    if (current.status === 'In Progress') return 'Field work is active. Watch photos, materials, notes, and completion status.';
    if (current.status === 'Paused') return this.pauseReasonText(current);
    if (current.status === 'Quote Sent') return 'Quote is with the customer. Follow up acceptance or decline.';
    if (current.status === 'Ready for Office') return 'Review evidence, quote, materials, and notes before marking completed.';
    if (current.status === 'Completed') return 'Job is closed. Confirm customer feedback has been handled.';
    if (current.status === 'Terminated') return 'Job is closed as terminated.';
    return 'Review the job detail and choose the next valid action.';
  }

  jobControlActionClass(): string {
    const current = this.job();
    if (!current) return 'bg-slate-50 text-slate-700 border-slate-100';
    if (this.latestSafetyStatus() === 'Declined' || current.status === 'Paused') return 'bg-orange-50 text-orange-800 border-orange-100';
    if (current.status === 'Unassigned' || current.status === 'Approved') return 'bg-amber-50 text-amber-800 border-amber-100';
    if (current.status === 'In Progress') return 'bg-emerald-50 text-emerald-800 border-emerald-100';
    if (current.status === 'Ready for Office') return 'bg-sky-50 text-sky-800 border-sky-100';
    return 'bg-slate-50 text-slate-700 border-slate-100';
  }

  pauseReasonText(current: any = this.job()): string {
    if (!current) return 'Field work is paused.';
    const reason = current.pause_category || 'Paused';
    const remarks = current.pause_remarks || 'No pause remarks recorded.';
    return `${reason}: ${remarks}`;
  }

  scheduleHistoryDetail(change: any): string {
    const oldDate = change.old_scheduled_date ? this.formatDate(change.old_scheduled_date) : 'Unscheduled';
    const newDate = change.new_scheduled_date ? this.formatDate(change.new_scheduled_date) : 'Cleared';
    const by = change.changed_by_name ? ` by ${change.changed_by_name}` : '';
    return `${oldDate} -> ${newDate}${by}. ${change.change_category}: ${change.remarks}`;
  }

  inspectionFlowStatusLabel(): string {
    const current = this.job();
    if (!current) return '';
    if (current.type === 'Service' && current.linked_inspection_id) return `This service job is linked to inspection ${current.linked_inspection_id}.`;
    if (current.type !== 'Inspection') return '';
    if (this.inspectionNoServiceRequired()) return 'Inspection closed: no follow-up service required.';
    if (this.linkedServiceJobs().length > 0) return 'Linked service work has been created from this inspection.';
    if (current.status === 'Quote Sent') return 'Inspection complete and quote sent to customer.';
    if (current.status === 'Approved') return 'Quote accepted. Create linked service work or record no service required.';
    return 'Inspection flow: inspect, quote, customer approval, then linked service work.';
  }

  inspectionFlowSteps(): Array<{ label: string; done: boolean }> {
    const current = this.job();
    if (!current) return [];
    if (current.type === 'Service' && current.linked_inspection_id) {
      return [
        { label: 'Inspection', done: true },
        { label: 'Service Created', done: true },
        { label: current.status || 'Service Work', done: ['In Progress', 'Ready for Office', 'Completed'].includes(current.status) }
      ];
    }
    const hasQuote = !!current.quote;
    const quoteSent = ['Quote Sent', 'Approved', 'Completed'].includes(current.status) || ['Sent', 'Accepted'].includes(current.quote?.status);
    const quoteAccepted = current.status === 'Approved' || current.quote?.status === 'Accepted' || this.linkedServiceJobs().length > 0;
    const noServiceRequired = this.inspectionNoServiceRequired();
    return [
      { label: 'Inspection', done: ['In Progress', 'Quote Sent', 'Approved', 'Ready for Office', 'Completed'].includes(current.status) || hasQuote },
      { label: 'Quote', done: hasQuote },
      { label: 'Customer Sent', done: quoteSent },
      { label: 'Accepted', done: quoteAccepted },
      { label: noServiceRequired ? 'No Service' : 'Service Job', done: this.linkedServiceJobs().length > 0 || noServiceRequired }
    ];
  }

  saveNoServiceRequired() {
    const id = this.job()?.id;
    const remarks = this.noServiceRemarks.trim();
    if (!id || this.isSaving() || this.isArchived()) return;
    if (remarks.length < 5) {
      showToast('error', 'Please add remarks for this decision.');
      return;
    }
    this.isSaving.set(true);
    this.api.saveInspectionServiceDecision({ id, decision: 'No Service Required', remarks }).subscribe({
      next: (res: any) => {
        this.isSaving.set(false);
        if (res.success) {
          showToast('success', 'Inspection service decision saved.');
          this.closeNoServiceModal();
          this.loadData(id.toString());
        } else {
          showToast('error', res.message || 'Decision could not be saved.');
        }
      },
      error: (err: any) => {
        this.isSaving.set(false);
        showToast('error', err?.error?.message || 'Decision could not be saved.');
      }
    });
  }

  changeJobStatus(status: string) {
    const id = this.job()?.id;
    if (!id || this.isSaving() || this.isArchived()) return;
    if (this.job()?.status === 'Paused' && status === 'In Progress') {
      this.openPauseResolutionModal();
      return;
    }
    this.isSaving.set(true);
    this.api.updateJobStatus({ id, status }).subscribe({
      next: (res: any) => {
        this.isSaving.set(false);
        if (res.success) {
          this.showJobStatusResult(status, res);
          this.loadData(id);
        } else {
          showToast('error', res.message || 'Job status could not be updated.');
        }
      },
      error: (err: any) => {
        this.isSaving.set(false);
        showToast('error', err?.error?.message || 'Job status could not be updated.');
      }
    });
  }

  updateMaterialReadiness(mat: any, readinessStatus: string) {
    if (!mat?.id || this.isSaving() || this.isArchived()) return;
    if (['Unavailable', 'Supplier Order Needed'].includes(readinessStatus) && !(mat.readiness_note || '').trim()) {
      const note = typeof window !== 'undefined' ? window.prompt('Add a short note for this parts issue:', mat.readiness_note || '') : '';
      if (!note || note.trim().length < 5) {
        showToast('error', 'A note is required for unavailable or supplier-order parts.');
        return;
      }
      mat.readiness_note = note.trim();
    }
    const id = (this.job()?.id || '').toString();
    this.isSaving.set(true);
    this.api.updateJobMaterialReadiness({
      id: mat.id,
      readiness_status: readinessStatus,
      readiness_note: mat.readiness_note || '',
      required_by: mat.required_by || null
    }).subscribe({
      next: (res: any) => {
        this.isSaving.set(false);
        if (res.success) {
          showToast('success', 'Material readiness updated.');
          this.loadData(id);
        } else {
          showToast('error', res.message || 'Material readiness could not be updated.');
        }
      },
      error: (err: any) => {
        this.isSaving.set(false);
        showToast('error', err?.error?.message || 'Material readiness could not be updated.');
      }
    });
  }

  resumePausedJob() {
    const id = this.job()?.id;
    const note = this.pauseResolutionNotes.trim();
    if (!id || this.isSaving() || this.isArchived() || this.job()?.status !== 'Paused' || note.length < 5) return;
    this.isSaving.set(true);
    this.api.updateJobStatus({ id, status: 'In Progress', pause_resolution_notes: note }).subscribe({
      next: (res: any) => {
        this.isSaving.set(false);
        if (res.success) {
          this.closePauseResolutionModal();
          showToast('success', 'Job resumed.');
          this.loadData(id);
        } else {
          showToast('error', res.message || 'Job could not be resumed.');
        }
      },
      error: (err: any) => {
        this.isSaving.set(false);
        showToast('error', err?.error?.message || 'Job could not be resumed.');
      }
    });
  }

  markCompleted() {
    if (!this.job()?.id || this.isSaving() || this.isArchived()) return;
    this.openCloseoutModal();
  }

  submitCloseout() {
    const id = this.job()?.id;
    if (!id || this.isSaving() || this.isArchived() || this.closeoutInvalid()) {
      if (this.officeCloseoutNote.trim().length < 5) {
        showToast('error', 'Final office close-out note is required.');
      } else if (this.officeBlockers().length > 0) {
        showToast('error', 'Exception remarks are required because checklist blockers remain.');
      }
      return;
    }
    this.isSaving.set(true);
    this.api.updateJobStatus({
      id,
      status: 'Completed',
      office_closeout_note: this.officeCloseoutNote.trim(),
      office_exception_remarks: this.officeExceptionRemarks.trim()
    }).subscribe({
      next: (res: any) => {
        this.isSaving.set(false);
        if (res.success) {
          this.closeCloseoutModal();
          this.showJobStatusResult('Completed', res);
          this.loadData(id);
        } else {
          showToast('error', res.message || 'Failed to complete job.');
        }
      },
      error: () => {
        this.isSaving.set(false);
        showToast('error', 'Network error completing job.');
      }
    });
  }

  private showJobStatusResult(status: string, res: any) {
    const feedback = res?.data?.feedback_request;
    if (feedback?.action_required) {
      showToast('success', status === 'Completed' ? 'Job completed. The customer review request is waiting to be sent.' : 'Job status updated. The customer review request is ready.');
      return;
    }
    showToast('success', status === 'In Progress' ? 'Job started.' : 'Job status updated.');
  }

  sendFeedbackRequest(customerEmail?: string) {
    const id = this.job()?.id;
    if (!id || this.isSaving() || this.isArchived()) return;
    const existingEmail = String(customerEmail || this.customerContact()?.email || '').trim().toLowerCase();
    if (!this.validCustomerEmail(existingEmail)) {
      const enteredEmail = window.prompt('Enter the customer email address for this review request:', existingEmail);
      if (enteredEmail === null) return;
      const normalizedEmail = enteredEmail.trim().toLowerCase();
      if (!this.validCustomerEmail(normalizedEmail)) {
        showToast('error', 'Enter a valid customer email address.');
        return;
      }
      this.sendFeedbackRequest(normalizedEmail);
      return;
    }
    this.isSaving.set(true);
    this.api.sendFeedbackRequest({ reference_id: id, model_type: 'Job', customer_email: existingEmail }).subscribe({
      next: (res: any) => {
        this.isSaving.set(false);
        if (res.success) {
          showToast('success', 'Review request sent to customer.');
          this.loadData(id.toString());
        } else {
          showToast('error', res.message || 'Feedback request could not be sent.');
        }
      },
      error: (err: any) => {
        this.isSaving.set(false);
        if (err?.error?.data?.email_required) {
          const enteredEmail = window.prompt('Enter the customer email address for this review request:', existingEmail);
          if (enteredEmail !== null && this.validCustomerEmail(enteredEmail.trim().toLowerCase())) {
            this.sendFeedbackRequest(enteredEmail.trim().toLowerCase());
          } else if (enteredEmail !== null) {
            showToast('error', 'Enter a valid customer email address.');
          }
          return;
        }
        showToast('error', err?.error?.message || 'Feedback request could not be sent.');
      }
    });
  }

  private validCustomerEmail(value: string): boolean {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
  }

  archiveJob() {
    const id = this.job()?.id;
    const remarks = this.archiveRemarks.trim();
    if (!id || this.isSaving() || this.isArchived() || !this.canManageArchive()) return;
    if (remarks.length < 5) {
      showToast('error', 'Please add archive remarks before archiving.');
      return;
    }
    this.isSaving.set(true);
    this.api.archiveReference({ reference_id: id, model_type: 'Job', remarks }).subscribe({
      next: (res: any) => {
        this.isSaving.set(false);
        if (res.success) {
          showToast('success', 'Job archived.');
          this.closeArchiveModal();
          this.loadData(id.toString());
        } else {
          showToast('error', res.message || 'Job could not be archived.');
        }
      },
      error: (err: any) => {
        this.isSaving.set(false);
        showToast('error', err?.error?.message || 'Job could not be archived.');
      }
    });
  }

  createInvoiceFromAcceptedQuote() {
    const quoteId = this.job()?.quote?.id;
    if (!quoteId) {
      showToast('error', 'No accepted quote is linked to this job.');
      return;
    }
    this.isSaving.set(true);
    this.api.createInvoiceFromQuote(quoteId).subscribe({
      next: (res: any) => {
        this.isSaving.set(false);
        if (res.success && res.data?.id) {
          showToast('success', 'Invoice created from accepted quote');
          this.router.navigate(['/admin/financials/invoices', res.data.id]);
        } else {
          showToast('error', res.message || 'Failed to create invoice');
        }
      },
      error: () => {
        this.isSaving.set(false);
        showToast('error', 'Failed to create invoice');
      }
    });
  }
}

